// Zero-Login Smart Drop Portal & Staff Intake client (shared types, deterministic preview classifier,
// Laya 16-class document triage highlights, borrower grouping, fast 256 KB file payload reader, and API helpers).
import { api, ApiError } from "./api";

export type PortalRole = "broker_lo" | "borrower" | "loan_processor";
export type UploaderRole = PortalRole | "ae" | "credit_analyst" | "account_manager";
export type DocCategory =
  | "application"
  | "credit"
  | "income"
  | "assets"
  | "property"
  | "title_insurance"
  | "disclosures"
  | "conditions"
  | "other";
export type FourLegsPillar = "income" | "credit" | "equity" | "assets" | "conditions" | "application";
export type RoutedSeat = "AE" | "CA" | "AM" | "Processor";
export type ReviewStatus =
  | "pending_review"
  | "accepted"
  | "bound_to_loan"
  | "attached_to_condition"
  | "archived";

/** Laya 16-class document triage families (`experiments/laya_request_triage/documents/`). */
export type LayaDocFamily =
  | "urla_1003"
  | "bank_statement"
  | "credit_report"
  | "tax_return_1040"
  | "business_tax_return"
  | "w2_1099_paystub"
  | "profit_and_loss"
  | "lease_rent_roll"
  | "asset_verification"
  | "appraisal_valuation"
  | "purchase_contract"
  | "title_commitment"
  | "hazard_insurance"
  | "entity_formation"
  | "condition_stip_loe"
  | "mixed_page"
  | (string & {});

export const LAYA_ARCHIVE_C9_CHECKPOINT =
  "laya-archive-c9-document-residual_mlp-refit-20261030T144212Z";
export const LAYA_ARCHIVE_C9_EMAIL_CHECKPOINT =
  "laya-archive-c9-email-residual_mlp-refit-20261030T144212Z";
export const LAYA_ARCHIVE_C8_CHECKPOINT = LAYA_ARCHIVE_C9_CHECKPOINT;
export const LAYA_ARCHIVE_C8_EMAIL_CHECKPOINT = LAYA_ARCHIVE_C9_EMAIL_CHECKPOINT;
export const LAYA_ARCHIVE_C7_CHECKPOINT = LAYA_ARCHIVE_C9_CHECKPOINT;
export const LAYA_ARCHIVE_C7_EMAIL_CHECKPOINT = LAYA_ARCHIVE_C9_EMAIL_CHECKPOINT;
export const LAYA_ARCHIVE_C6_CHECKPOINT = LAYA_ARCHIVE_C9_CHECKPOINT;
export const LAYA_ARCHIVE_C6_EMAIL_CHECKPOINT = LAYA_ARCHIVE_C9_EMAIL_CHECKPOINT;
export const LAYA_ARCHIVE_C5_CHECKPOINT = LAYA_ARCHIVE_C9_CHECKPOINT;
export const LAYA_ARCHIVE_C5_EMAIL_CHECKPOINT = LAYA_ARCHIVE_C9_EMAIL_CHECKPOINT;
export const LAYA_ARCHIVE_C4_CHECKPOINT = LAYA_ARCHIVE_C9_CHECKPOINT;
export const LAYA_ARCHIVE_C3_CHECKPOINT = LAYA_ARCHIVE_C9_CHECKPOINT;

export interface DocumentCompletenessWarning {
  code:
    | "placeholder_or_removed_document"
    | "signature_or_identity_redacted"
    | "truncated_or_missing_pages"
    | "image_only_scan_ocr_needed"
    | "garbled_font_encoding";
  severity: "warning" | "info";
  message: string;
  missing_pages?: number[];
}

export interface DocumentCompletenessDiagnostics {
  status: "complete" | "attention_needed";
  placeholder_or_removed_document: boolean;
  signature_or_identity_redacted: boolean;
  truncated_or_missing_pages: boolean;
  image_only_scan_ocr_needed: boolean;
  garbled_font_encoding: boolean;
  defect_codes: string[];
  warnings: DocumentCompletenessWarning[];
}

export interface EmailTriageMetadata {
  subject: string | null;
  from: string | null;
  to: string | null;
  attachment_filenames: string[];
  has_current_document_attachment: boolean;
  actionability: "potential_action" | "information_only" | "unclear";
  email_model_checkpoint?: string;
}

export interface LayaExtractedHighlights {
  laya_family?: LayaDocFamily | null;
  secondary_laya_families?: string[];
  is_multi_document_bundle?: boolean;
  statement_period?: string | null;
  segmentation_guard_triggered?: boolean;
  model_checkpoint?: string;
  structural_features?: string[];
  email_triage?: EmailTriageMetadata | null;
  document_completeness?: DocumentCompletenessDiagnostics;
  intake_warnings?: DocumentCompletenessWarning[];
  authority: "advisory_unreviewed";
  originals_retained: true;
  lending_decision: "not_made";
  [key: string]: unknown;
}

export interface SmartDropItem {
  id: string;
  link_id: string | null;
  batch_id: string;
  uploader_role: UploaderRole;
  uploader_label: string;
  person_id: string | null;
  company_id: string | null;
  lo_name: string | null;
  company_name: string | null;
  owner_user_id: string | null;
  loan_id: string | null;
  matched_condition_id: string | null;
  file_name: string;
  mime_type: string;
  byte_size: number;
  sha256: string;
  detected_borrower_name: string;
  detected_property_address: string | null;
  detected_loan_number: string | null;
  doc_category: DocCategory;
  doc_type_label: string;
  four_legs_pillar: FourLegsPillar;
  routed_seat: RoutedSeat;
  routed_user_id: string | null;
  routed_user_name: string | null;
  routing_reason: string;
  confidence: "high" | "medium" | "review_needed";
  extracted_highlights: Record<string, unknown>;
  review_status: ReviewStatus;
  review_note: string | null;
  created_at: string;
}

export interface SmartDropLink {
  id: string;
  token_prefix: string;
  token: string;
  token_cleartext?: string;
  portal_role: PortalRole;
  person_id: string | null;
  company_id: string | null;
  lo_name: string;
  company_name: string | null;
  loan_id: string | null;
  deal_link_id: string | null;
  label: string;
  assigned_ae_name: string | null;
  assigned_ca_name: string | null;
  assigned_am_name: string | null;
  upload_count: number;
  last_upload_at: string | null;
  expires_at: string;
  revoked_at: string | null;
  created_at: string;
}

export interface PublicDropPortalInfo {
  link_id: string;
  token_prefix: string;
  portal_role: PortalRole;
  label: string;
  lo_name: string;
  company_name: string | null;
  loan_id: string | null;
  team_contacts: {
    ae_name: string | null;
    ca_name: string | null;
    am_name: string | null;
  };
  active_deals: Array<{
    loan_id: string;
    borrower_label: string;
    loan_number: string | null;
    stage_label: string;
    open_conditions: Array<{
      condition_id: string;
      text: string;
    }>;
  }>;
  recent_items: SmartDropItem[];
  expires_at: string;
  /** The link is bound to a recipient email: the portal must verify it (`POST /api/public/drop/{token}/verify-email`). */
  email_verification_required?: boolean;
  email_verified?: boolean;
  /** Masked recipient address (`j***@example.com`); the server never sends the full address. */
  recipient_email_hint?: string | null;
}

export interface SmartDropInboxResponse {
  items: SmartDropItem[];
  counts_by_seat: Record<string, number>;
  borrower_groups: Array<{
    borrower_name: string;
    lo_name: string | null;
    company_name: string | null;
    matched_loan_id: string | null;
    item_count: number;
    routed_seats: RoutedSeat[];
  }>;
}

export interface SmartDropPreviewResult {
  detectedBorrowerName: string;
  docCategory: DocCategory;
  docTypeLabel: string;
  fourLegsPillar: FourLegsPillar;
  routedSeat: RoutedSeat;
  routingReason: string;
  layaFamily: LayaDocFamily;
  statementPeriod: string | null;
  segmentationGuardTriggered: boolean;
  extractedHighlights: LayaExtractedHighlights;
}

export interface BorrowerGroup {
  borrowerName: string;
  items: SmartDropItem[];
  routedSeats: RoutedSeat[];
  pillars: FourLegsPillar[];
  matchedLoanId: string | null;
  loName: string | null;
  companyName: string | null;
}

export interface SmartDropFilePayload {
  file_name: string;
  mime_type: string;
  byte_size: number;
  content_base64: string;
  text_excerpt: string;
}

export interface PublicDropUploadPayload {
  uploader_role?: UploaderRole;
  uploader_label?: string;
  borrower_name?: string;
  property_address?: string;
  loan_id?: string | null;
  condition_id?: string | null;
  matched_condition_id?: string | null;
  note?: string;
  files: SmartDropFilePayload[];
}

export interface StaffSmartDropUploadPayload {
  uploader_role?: UploaderRole;
  uploader_label?: string;
  person_id?: string | null;
  company_id?: string | null;
  lo_name?: string | null;
  company_name?: string | null;
  loan_id?: string | null;
  condition_id?: string | null;
  matched_condition_id?: string | null;
  borrower_name?: string;
  property_address?: string;
  note?: string;
  files: SmartDropFilePayload[];
}

export const PORTAL_ROLE_LABEL: Record<PortalRole, string> = {
  broker_lo: "Loan Officer / Broker",
  loan_processor: "Loan Processor",
  borrower: "Borrower",
};

export const UPLOADER_ROLE_LABEL: Record<UploaderRole, string> = {
  broker_lo: "Loan Officer / Broker",
  loan_processor: "Loan Processor",
  borrower: "Borrower",
  ae: "Account Executive",
  credit_analyst: "Credit Analyst",
  account_manager: "Account Manager",
};

export const DOC_CATEGORY_LABEL: Record<DocCategory, string> = {
  application: "Application",
  credit: "Credit",
  income: "Income",
  assets: "Assets",
  property: "Property",
  title_insurance: "Title & Insurance",
  disclosures: "Disclosures",
  conditions: "Conditions",
  other: "Other",
};

export const FOUR_LEGS_PILLAR_LABEL: Record<FourLegsPillar, string> = {
  income: "Income",
  credit: "Credit",
  equity: "Equity",
  assets: "Assets",
  conditions: "Conditions",
  application: "Application",
};

export const ROUTED_SEAT_LABEL: Record<RoutedSeat, string> = {
  AE: "Routed to AE",
  CA: "Routed to CA",
  AM: "Routed to AM",
  Processor: "Routed to Processor",
};

export const REVIEW_STATUS_LABEL: Record<ReviewStatus, string> = {
  pending_review: "Pending Review",
  accepted: "Accepted",
  bound_to_loan: "Bound to Loan",
  attached_to_condition: "Attached to Condition",
  archived: "Archived",
};

export const LAYA_FAMILY_LABEL: Record<string, string> = {
  urla_1003: "1003 URLA Application",
  bank_statement: "Bank Statement",
  credit_report: "Tri-Merge Credit Report",
  tax_return_1040: "1040 Personal Tax Return",
  business_tax_return: "Business Tax Return (1120/1065/K-1)",
  w2_1099_paystub: "W-2 / 1099 / Paystub",
  profit_and_loss: "Profit & Loss (P&L)",
  lease_rent_roll: "Lease / Rent Roll",
  asset_verification: "Asset / VOD Statement",
  appraisal_valuation: "Appraisal / Valuation",
  purchase_contract: "Purchase Contract",
  title_commitment: "Title Commitment",
  hazard_insurance: "Hazard / Flood Insurance",
  entity_formation: "Entity Formation / Operating Agreement",
  condition_stip_loe: "Condition Stip / LOE",
  mixed_page: "mixed_page (Segmentation Hold)",
};

export function formatLayaFamily(family: string | null | undefined): string | null {
  if (!family || !family.trim()) return null;
  const key = family.trim();
  return LAYA_FAMILY_LABEL[key] ?? key;
}

const MONTH_NAME_TO_NUM: Record<string, string> = {
  jan: "01",
  january: "01",
  feb: "02",
  february: "02",
  mar: "03",
  march: "03",
  apr: "04",
  april: "04",
  may: "05",
  jun: "06",
  june: "06",
  jul: "07",
  july: "07",
  aug: "08",
  august: "08",
  sep: "09",
  sept: "09",
  september: "09",
  oct: "10",
  october: "10",
  nov: "11",
  november: "11",
  dec: "12",
  december: "12",
};

function daysInMonth(year: number, month: number): number {
  if ([1, 3, 5, 7, 8, 10, 12].includes(month)) return 31;
  if ([4, 6, 9, 11].includes(month)) return 30;
  if (month === 2) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28;
  }
  return 0;
}

function formatIsoYmd(year: number, month: number, day: number): string | null {
  if (year >= 2015 && year <= 2035 && month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month)) {
    return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  return null;
}

function parseFlexibleDateString(raw: string): string | null {
  const s = raw.trim().replace(/^[.,;:([\s]+|[.,;:)\]\s]+$/g, "");
  const isoMatch = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.exec(s);
  if (isoMatch) {
    return formatIsoYmd(
      Number.parseInt(isoMatch[1], 10),
      Number.parseInt(isoMatch[2], 10),
      Number.parseInt(isoMatch[3], 10),
    );
  }
  const slashMatch = /^(\d{1,2})\s*[/-]\s*(\d{1,2})\s*[/-]\s*(\d{2}|\d{4})$/.exec(s);
  if (slashMatch) {
    const m = Number.parseInt(slashMatch[1], 10);
    const d = Number.parseInt(slashMatch[2], 10);
    const rawY = Number.parseInt(slashMatch[3], 10);
    const y = slashMatch[3].length === 2 ? 2000 + rawY : rawY;
    return formatIsoYmd(y, m, d);
  }
  const monthMatch =
    /^(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/i.exec(
      s,
    );
  if (monthMatch) {
    const mStr = MONTH_NAME_TO_NUM[monthMatch[1].toLowerCase()];
    if (!mStr) return null;
    return formatIsoYmd(
      Number.parseInt(monthMatch[3], 10),
      Number.parseInt(mStr, 10),
      Number.parseInt(monthMatch[2], 10),
    );
  }
  const dayFirstMonthMatch =
    /^(\d{1,2})\s+(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?,?\s+(\d{4})$/i.exec(
      s,
    );
  if (dayFirstMonthMatch) {
    const mStr = MONTH_NAME_TO_NUM[dayFirstMonthMatch[2].toLowerCase()];
    if (!mStr) return null;
    return formatIsoYmd(
      Number.parseInt(dayFirstMonthMatch[3], 10),
      Number.parseInt(mStr, 10),
      Number.parseInt(dayFirstMonthMatch[1], 10),
    );
  }
  return null;
}

export interface StatementPeriodExtraction {
  status: "source_extracted" | "unresolved";
  start?: string;
  end?: string;
  reason?: string;
}

/**
 * Deterministically extracts a printed statement period in ISO (`YYYY-MM-DD`),
 * US slash/dash (`MM/DD/YYYY`, `M/D/YY`, `8 / 03 / 26`), or Month-name (`September 1, 2024 - September 30, 2024`,
 * `July 1-31, 2026`, `Jul 1 - Jul 31, 2026`, `01 Jul. 2026 al 31 Jul. 2026`)
 * format and normalizes `start` and `end` to `YYYY-MM-DD` without fabricating dates.
 */
export function extractStatementPeriod(text: string): StatementPeriodExtraction {
  if (!text || !text.trim()) {
    return { status: "unresolved", reason: "no_supported_printed_period" };
  }

  // 1. Strict ISO YYYY-MM-DD pairs
  const isoTokens = text.match(/\b20\d{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])\b/g);
  if (isoTokens && isoTokens.length >= 2) {
    return {
      status: "source_extracted",
      start: isoTokens[0],
      end: isoTokens[1],
    };
  }

  // 1b. Compact Month D1-D2, YYYY (e.g. Schwab "Statement Period July 1-31, 2026")
  const compactMonthRange =
    /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+(\d{1,2})\s*[-–—]\s*(\d{1,2}),?\s+(20\d{2})\b/i.exec(
      text,
    );
  if (compactMonthRange) {
    const mStr = MONTH_NAME_TO_NUM[compactMonthRange[1].toLowerCase()];
    const y = Number.parseInt(compactMonthRange[4], 10);
    const m = mStr ? Number.parseInt(mStr, 10) : 0;
    const d1 = Number.parseInt(compactMonthRange[2], 10);
    const d2 = Number.parseInt(compactMonthRange[3], 10);
    const iso1 = formatIsoYmd(y, m, d1);
    const iso2 = formatIsoYmd(y, m, d2);
    if (iso1 && iso2) {
      const [start, end] = iso1 <= iso2 ? [iso1, iso2] : [iso2, iso1];
      return { status: "source_extracted", start, end };
    }
  }

  // 1c. Month1 D1 -/to Month2 D2, YYYY (e.g. Apple Card "Jul 1 - Jul 31, 2026" or VeraBank "aug 1 to aug 13 2026")
  const twoMonthSharedYearRange =
    /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+(\d{1,2})\s*(?:to|through|thru|[-–—])\s*(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+(\d{1,2}),?\s+(20\d{2})\b/i.exec(
      text,
    );
  if (twoMonthSharedYearRange) {
    const m1Str = MONTH_NAME_TO_NUM[twoMonthSharedYearRange[1].toLowerCase()];
    const m2Str = MONTH_NAME_TO_NUM[twoMonthSharedYearRange[3].toLowerCase()];
    const y2 = Number.parseInt(twoMonthSharedYearRange[5], 10);
    const m1 = m1Str ? Number.parseInt(m1Str, 10) : 0;
    const m2 = m2Str ? Number.parseInt(m2Str, 10) : 0;
    const d1 = Number.parseInt(twoMonthSharedYearRange[2], 10);
    const d2 = Number.parseInt(twoMonthSharedYearRange[4], 10);
    const y1 = m1 > m2 ? y2 - 1 : y2;
    const iso1 = formatIsoYmd(y1, m1, d1);
    const iso2 = formatIsoYmd(y2, m2, d2);
    if (iso1 && iso2) {
      const [start, end] = iso1 <= iso2 ? [iso1, iso2] : [iso2, iso1];
      return { status: "source_extracted", start, end };
    }
  }

  const datePat =
    "(?:20\\d{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\\d|3[01])|\\d{1,2}\\s*[/\\-]\\s*\\d{1,2}\\s*[/\\-]\\s*(?:\\d{4}|\\d{2})|(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\\.?\\s+\\d{1,2}(?:st|nd|rd|th)?,?\\s+20\\d{2}|\\d{1,2}\\s+(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\\.?,?\\s+20\\d{2})";
  const rangeRegex = new RegExp(
    `(${datePat})\\s*(?:to|through|thru|al|t\\s*h\\s*r\\s*u|[-–—])\\s*(${datePat})`,
    "gi",
  );
  let rMatch: RegExpExecArray | null = rangeRegex.exec(text);
  while (rMatch !== null) {
    const preContext = text.slice(Math.max(0, rMatch.index - 24), rMatch.index).toLowerCase();
    if (!/\bfee\b/.test(preContext)) {
      const d1 = parseFlexibleDateString(rMatch[1]);
      const d2 = parseFlexibleDateString(rMatch[2]);
      if (d1 && d2) {
        const [start, end] = d1 <= d2 ? [d1, d2] : [d2, d1];
        return { status: "source_extracted", start, end };
      }
    }
    rMatch = rangeRegex.exec(text);
  }

  // 2. Beginning / Opening balance on date ... Ending / Closing balance on date
  const begEndFullRegex = new RegExp(
    `(?:beginning|opening|previous)\\s+(?:balance|amount)(?:\\s+(?:on|as\\s+of))?[\\s:]*` +
      `(${datePat})[\\s\\S]{0,240}?(?:ending|closing|new)\\s+(?:balance|amount)(?:\\s+(?:on|as\\s+of))?[\\s:]*` +
      `(${datePat})`,
    "i",
  );
  const beFull = begEndFullRegex.exec(text);
  if (beFull) {
    const d1 = parseFlexibleDateString(beFull[1]);
    const d2 = parseFlexibleDateString(beFull[2]);
    if (d1 && d2) {
      const [start, end] = d1 <= d2 ? [d1, d2] : [d2, d1];
      return { status: "source_extracted", start, end };
    }
  }

  // 3. Short M/D with header year (e.g. "September 2025 ... Beginning balance on 9/1 ... Ending balance on 9/30")
  const yearMatch = /\b(202\d)\b/.exec(text.slice(0, 600));
  if (yearMatch) {
    const headerYear = Number.parseInt(yearMatch[1], 10);
    const shortMdRegex =
      /(?:beginning|opening)\s+balance\s+on\s+(\d{1,2})\/(\d{1,2})[\s\S]{0,260}?(?:ending|closing)\s+balance\s+on\s+(\d{1,2})\/(\d{1,2})/i.exec(
        text,
      );
    if (shortMdRegex) {
      const m1 = Number.parseInt(shortMdRegex[1], 10);
      const d1 = Number.parseInt(shortMdRegex[2], 10);
      const m2 = Number.parseInt(shortMdRegex[3], 10);
      const d2 = Number.parseInt(shortMdRegex[4], 10);
      const y1 = m1 > m2 ? headerYear - 1 : headerYear;
      const iso1 = formatIsoYmd(y1, m1, d1);
      const iso2 = formatIsoYmd(headerYear, m2, d2);
      if (iso1 && iso2) {
        return { status: "source_extracted", start: iso1, end: iso2 };
      }
    }
  }

  // 4. Single statement/closing date header -> derive calendar month period
  const singleDateRegex = new RegExp(
    `(?:statement\\s+date|closing\\s+date|as\\s+of)[\\s:]*(${datePat})`,
    "i",
  );
  const singleMatch = singleDateRegex.exec(text);
  if (singleMatch) {
    const endIso = parseFlexibleDateString(singleMatch[1]);
    if (endIso) {
      const [yStr, mStr] = endIso.split("-");
      const startIso = `${yStr}-${mStr}-01`;
      return { status: "source_extracted", start: startIso, end: endIso };
    }
  }

  // 5. Numeric month serial period ("period 24310" where 24310 = 2025 * 12 + 10 -> 2025-11)
  const serialMatch = /\bperiod[\s:]+(24[234]\d{2})\b/i.exec(text);
  if (serialMatch?.[1]) {
    const serial = Number.parseInt(serialMatch[1], 10);
    if (serial >= 24240 && serial <= 24420) {
      const year = Math.floor(serial / 12);
      const month = (serial % 12) + 1;
      const start = formatIsoYmd(year, month, 1);
      const end = formatIsoYmd(year, month, daysInMonth(year, month));
      if (start && end) {
        return { status: "source_extracted", start, end };
      }
    }
  }

  return { status: "unresolved", reason: "no_supported_printed_period" };
}

/**
 * Extracts Laya's 30 canonical closed-vocabulary structural features (`laya.document-structure/1`)
 * deterministically from filename and document text without inventing underwriting rules.
 */
export function extractLayaStructuralFeatures(text: string): string[] {
  const lower = (text || "").toLowerCase();
  const out: string[] = [];

  if (
    /(?:deposit account|checking|savings|member fdic|bank statement|bank-statement|bankstmt|combined statement of accounts|account summary|account ending in|thrift savings plan|venmo \| account statement)/.test(
      lower,
    )
  ) {
    out.push("deposit_account");
  }
  if (
    /(?:opening balance|beginning balance|previous balance|beginning amount|balance forward|starting balance)/.test(
      lower,
    )
  ) {
    out.push("opening_balance");
  }
  if (
    /(?:closing balance|ending balance|new balance|ending amount|current balance|total account balance)/.test(
      lower,
    )
  ) {
    out.push("closing_balance");
  }
  if (
    /(?:deposits and credits|deposits & credits|deposits\/credits|total deposits|electronic deposits|direct deposit|other credits)/.test(
      lower,
    ) ||
    (/\bdeposits\b/.test(lower) && /\bcredits\b/.test(lower))
  ) {
    out.push("deposits_credits");
  }
  if (
    /(?:checks and withdrawals|checks & withdrawals|withdrawals and debits|electronic withdrawals|withdrawals and subtractions|other withdrawals|checks paid|debits)/.test(
      lower,
    )
  ) {
    out.push("checks_withdrawals");
  }
  if (
    /(?:statement period|statement date|billing cycle|for the period|activity summary)/.test(
      lower,
    ) ||
    extractStatementPeriod(text).status === "source_extracted"
  ) {
    out.push("statement_period_field");
  }
  if (
    /(?:pay period|pay date|pay stub|paystub|earnings statement|period ending|advice date)/.test(
      lower,
    )
  ) {
    out.push("pay_period_field");
  }
  if (
    /(?:year to date|year-to-date|ytd gross|ytd net|gross pay|net pay|regular earnings)/.test(
      lower,
    ) ||
    /\bytd\b/.test(lower)
  ) {
    out.push("year_to_date_earnings");
  }
  if (
    /(?:federal income tax|social security tax|medicare tax|tax withholdings|withholding|fed med\/ee|fed oasdi\/ee|fica)/.test(
      lower,
    )
  ) {
    out.push("tax_withholdings");
  }
  if (
    /(?:tradeline|tradelines|tri-merge|trimerge|experian|equifax|transunion|fico|revolving accounts|installment accounts)/.test(
      lower,
    )
  ) {
    out.push("credit_tradelines");
  }
  if (/(?:inquiries|credit inquiry|inquiry loe|facta|recent inquiries)/.test(lower)) {
    out.push("bureau_inquiries");
  }
  if (/(?:payment history|30\/60\/90|times 30|times 60|times 90|derogatory)/.test(lower)) {
    out.push("payment_history_grid");
  }
  if (
    /(?:credit card|revolving credit|cash advance limit|available credit|mastercard|visa statement)/.test(
      lower,
    )
  ) {
    out.push("revolving_credit_card");
  }
  if (/(?:minimum payment warning|minimum payment due|late payment warning)/.test(lower)) {
    out.push("minimum_payment_warning");
  }
  if (
    /(?:mortgage statement|unpaid principal balance|interest rate|maturity date|loan payoff|beneficiary demand|select portfolio servicing)/.test(
      lower,
    )
  ) {
    out.push("mortgage_installment");
  }
  if (
    /(?:principal and interest|escrow balance|taxes and insurance|p&i|principal, interest, taxes|monthly p&i)/.test(
      lower,
    )
  ) {
    out.push("principal_interest_escrow");
  }
  if (
    /(?:u\.s\. individual income tax return|adjusted gross income|taxable income|tax return|1120s|1065)/.test(
      lower,
    )
  ) {
    out.push("income_tax_return");
  }
  if (/(?:form 1040|1040)/.test(lower)) {
    out.push("form_1040");
  }
  if (
    /(?:schedule c|schedule e|schedule_e|profit or loss from business|supplemental income and loss)/.test(
      lower,
    )
  ) {
    out.push("schedule_c_or_e");
  }
  if (/(?:form w-2|w-2|w2|wage and tax statement)/.test(lower)) {
    out.push("form_w2");
  }
  if (/(?:form 1099|1099-nec|1099-misc|1099-int|1099-div|1099|schedule k-1|k-1)/.test(lower)) {
    out.push("form_1099");
  }
  if (
    /(?:uniform residential loan application|urla|1003|mismo|<\?xml|<deal|<borrower)/.test(lower)
  ) {
    out.push("form_1003");
  }
  if (
    /(?:declarations page|dwelling coverage|hazard insurance|certificate of insurance|evidence of insurance|flood hazard|deductible|policy number|acord)/.test(
      lower,
    )
  ) {
    out.push("insurance_coverages");
  }
  if (
    /(?:comparable sale|sales comparison approach|appraised value|uniform residential appraisal report|1004|1025|1073|bpo|reconsideration of value|comparable properties)/.test(
      lower,
    )
  ) {
    out.push("appraisal_comparables");
  }
  if (
    /(?:lease agreement|rental agreement|monthly rent|security deposit|rent roll|tenant|landlord|1007|airdna|rentalizer|short-term rental|vacation rental)/.test(
      lower,
    )
  ) {
    out.push("lease_covenants");
  }
  if (/(?:utility bill|kwh|electric bill|water bill|gas bill|meter reading)/.test(lower)) {
    out.push("meter_consumption");
  }
  if (
    /(?:articles of organization|operating agreement|certificate of good standing|employer identification number|ein|limited liability company|secretary of state)/.test(
      lower,
    )
  ) {
    out.push("entity_formation");
  }
  if (
    /(?:self-employed questionnaire|pre-qualification|prequal|borrower information|assets and liabilities|declarations)/.test(
      lower,
    )
  ) {
    out.push("application_sections");
  }
  if (
    /(?:conditional loan approval|conditional approval|conditions of approval|underwriting conditions|prior to doc|prior to funding|ptd|ptf|letter of explanation|closing disclosure|loan estimate|settlement statement)/.test(
      lower,
    )
  ) {
    out.push("closing_conditions");
  }
  if (
    /(?:invoice|contractor bid|rehab budget|scope of work|draw schedule|amount due|line item)/.test(
      lower,
    )
  ) {
    out.push("invoice_line_items");
  }

  return out;
}

function extractTopEmailBody(normalized: string, hasTopHeaders: boolean): string {
  let bodyRaw = normalized;
  if (hasTopHeaders) {
    const crlfIdx = normalized.indexOf("\r\n\r\n");
    const lfIdx = normalized.indexOf("\n\n");
    if (crlfIdx !== -1 && (lfIdx === -1 || crlfIdx <= lfIdx)) {
      bodyRaw = normalized.slice(crlfIdx + 4);
    } else if (lfIdx !== -1) {
      bodyRaw = normalized.slice(lfIdx + 2);
    }
  }

  let s = bodyRaw
    .replace(/=\r?\n/g, "")
    .replace(/=3D/gi, "=")
    .replace(/=20/g, " ")
    .replace(/&nbsp;/gi, " ");

  const lower = s.toLowerCase();
  const cutCandidates = [
    lower.indexOf('<div id="appendonsend"'),
    lower.indexOf("-----original message-----"),
    lower.indexOf("-------- original message --------"),
    lower.indexOf("upload broker conditions here"),
    lower.indexOf("important note to brokers:"),
    lower.indexOf("apply for loan"),
  ].filter((idx) => idx !== -1);
  if (cutCandidates.length > 0) {
    s = s.slice(0, Math.min(...cutCandidates));
  }

  s = s
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<head[\s\S]*?<\/head>/gi, " ")
    .replace(/<[^>]+>/g, " ");

  const bodyLines: string[] = [];
  for (const rawLine of s.split(/\r?\n/)) {
    const trimmed = rawLine.trim();
    if (!trimmed) continue;
    const tl = trimmed.toLowerCase();
    if (
      tl.startsWith("--_00") ||
      tl.startsWith("content-type:") ||
      tl.startsWith("content-transfer-encoding:") ||
      tl.startsWith("content-disposition:") ||
      tl.startsWith("content-id:") ||
      tl.startsWith("mime-version:")
    ) {
      continue;
    }
    if (trimmed.length >= 60 && !trimmed.includes(" ")) {
      continue;
    }
    if (
      tl.startsWith("-----original message-----") ||
      tl.startsWith("-------- original message --------") ||
      (tl.startsWith("from:") && bodyLines.length > 0) ||
      (tl.startsWith("on ") && tl.endsWith("wrote:") && bodyLines.length > 0)
    ) {
      break;
    }
    bodyLines.push(trimmed);
    if (bodyLines.length >= 40) break;
  }

  return bodyLines.join(" ");
}

/**
 * Extracts deterministic RFC-822 / `.eml` email triage metadata (`subject`, `from`, `to`,
 * `attachment_filenames`, `has_current_document_attachment`, and `actionability`)
 * without inventing underwriting rules.
 */
export function extractEmailTriage(
  fileName: string,
  contentText?: string,
): EmailTriageMetadata | null {
  const lowerName = (fileName || "").toLowerCase();
  const rawText = contentText ?? "";
  const text = !rawText.includes("\n") && rawText.includes("\\n") ? rawText.replace(/\\n/g, "\n") : rawText;
  const isEmlFile = lowerName.endsWith(".eml");
  const hasEmailHeaders =
    /^(?:from|subject|to|date|received|mime-version|content-type):/im.test(text) &&
    /^(?:subject|from):/im.test(text);

  if (!isEmlFile && !hasEmailHeaders) {
    return null;
  }

  let subjectVal = "";
  let fromVal = "";
  let toVal = "";
  let activeHeader: "subject" | "from" | "to" | null = null;
  let seenAnyHeader = false;
  let inHeaders = true;

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.replace(/\r$/, "");
    if (inHeaders && line.trim().length === 0) {
      if (seenAnyHeader) {
        inHeaders = false;
        activeHeader = null;
      }
      continue;
    }
    if (inHeaders) {
      if ((line.startsWith(" ") || line.startsWith("\t")) && activeHeader) {
        const cont = line.trim();
        if (cont) {
          if (activeHeader === "subject") {
            subjectVal = subjectVal ? `${subjectVal} ${cont}` : cont;
          } else if (activeHeader === "from") {
            fromVal = fromVal ? `${fromVal} ${cont}` : cont;
          } else if (activeHeader === "to") {
            toVal = toVal ? `${toVal} ${cont}` : cont;
          }
        }
        continue;
      }
      const lineLower = line.toLowerCase();
      if (lineLower.startsWith("subject:")) {
        seenAnyHeader = true;
        if (!subjectVal) {
          subjectVal = line.slice(8).trim();
          activeHeader = "subject";
        } else {
          activeHeader = null;
        }
        continue;
      }
      if (lineLower.startsWith("from:")) {
        seenAnyHeader = true;
        if (!fromVal) {
          fromVal = line.slice(5).trim();
          activeHeader = "from";
        } else {
          activeHeader = null;
        }
        continue;
      }
      if (lineLower.startsWith("to:")) {
        seenAnyHeader = true;
        if (!toVal) {
          toVal = line.slice(3).trim();
          activeHeader = "to";
        } else {
          activeHeader = null;
        }
        continue;
      }
      if (/^(?:received|date|mime-version|content-type|message-id):/i.test(line)) {
        seenAnyHeader = true;
      }
      if (line.includes(":")) {
        activeHeader = null;
      }
    }
  }

  const from = fromVal || null;
  const to = toVal || null;
  let subject = subjectVal || null;
  if (!subject && isEmlFile) {
    subject = fileName
      .replace(/\.eml$/i, "")
      .replace(/^\d{8}-\d{4,6}-/, "")
      .replace(/[-_]+/g, " ")
      .trim();
  }

  const topBody = extractTopEmailBody(text, seenAnyHeader);
  const topBodyLower = topBody.toLowerCase();

  const attachmentSet = new Set<string>();
  const attRegex = /(?:filename|name)\*?\s*=\s*(?:"([^"]+)"|([^\s;\r\n)>]+))/gi;
  let m: RegExpExecArray | null = attRegex.exec(text);
  while (m !== null) {
    const rawAtt = (m[1] || m[2] || "").trim().replace(/^UTF-8''/i, "");
    const fl = rawAtt.toLowerCase();
    const isInlineSigImg =
      /^image\d*\.(?:png|jpg|jpeg|gif)$/i.test(fl) ||
      fl === "marketing.png" ||
      fl === "marketing.jpg" ||
      fl.startsWith("outlook-") ||
      fl.startsWith("att00") ||
      fl.startsWith("~wrd") ||
      fl.endsWith(".bin") ||
      (/\.(?:png|jpg|jpeg|gif)$/i.test(fl) && rawAtt.length > 28 && rawAtt.includes("-"));
    if (rawAtt && /\.[a-z0-9]{2,5}$/i.test(rawAtt) && !isInlineSigImg) {
      attachmentSet.add(rawAtt);
    }
    m = attRegex.exec(text);
  }

  const attachment_filenames = Array.from(attachmentSet);
  const hasDocAttachment = attachment_filenames.some((fn) =>
    /\.(?:pdf|xml|fnm|xlsx|xls|xlsm|csv|doc|docx|zip)$/i.test(fn),
  );
  const subjLower = (subject ?? "").toLowerCase();
  const attsLower = attachment_filenames.join(" ").toLowerCase();

  const isAutomatedDigest =
    (/\b(?:daily\s+production\s+report|pipeline\s+report|batch\s+\d+\s+processed|quarantine\s+digest|daily\s+digest|reaction\s+daily\s+digest|rate\s+sheets?|out\s+of\s+office|automatic\s+reply|in\s+office\s+today|doc\s+request\s+has\s+been\s+viewed|weekly\s+digest|newsletter|debrief|recap|sponsorship\s+opportunities|mortgage\s+expo|mortgagepros411)\b/.test(
      subjLower,
    ) ||
      /\b(?:inviting\s+you\s+to\s+a\s+scheduled\s+zoom\s+meeting|join\s+zoom\s+meeting|limited\s+access\s+to\s+email|become\s+a\s+sponsor\s+today)\b/.test(
        topBodyLower,
      )) &&
    !hasDocAttachment;

  const isReplyOrForward = /^(?:re|fw|fwd):/i.test(subjLower);
  const hasAckPhrase =
    /\b(?:thank\s+you|thanks|wonderful|fabulous|got\s+it|received|will\s+do|sounds\s+good|have\s+a\s+great\s+weekend|appreciate\s+it|acknowledged|well\s+played|good\s+teacher|love\s+it|lmfao|happy\s+friday)\b/.test(
      topBodyLower,
    );
  const hasSubstantiveBodySignal =
    /(?:\b(?:please|attached|attach|review|scenario|conditions?|waive|removed?|error|income|calculation|deposits?|urla|1003|ltv|dscr|rate|points|epo|appraisal|1004|title|insurance|credit|closing|push\s+in|le\s+prep|sign|e-consent|portal|approve|approval|question|need\s+to\s+close|for\s+recording)\b|\?)/.test(
      topBodyLower,
    );
  const isShortAckReply =
    !hasDocAttachment && isReplyOrForward && hasAckPhrase && !hasSubstantiveBodySignal;

  const hasActionSignal =
    hasDocAttachment ||
    /\b(?:attached|please\s+find|please\s+review|pre-?qual|prequal|pq\b|1003|urla|appraisal|bank\s+statement|credit\s+report|conditions?|stip|rush|review|approval|conditionally\s+approved|airdna|workup|scenario|quick\s+quote|quote|paystub|tax\s+return|closing|disclosures?|jumbo\s+loan|guc|for\s+prudent|urgent|signature|document\s+received|checking\/savings|loe|counter\s+offer|avoiding\s+epo|intent\s+to\s+proceed|refinance|bridge|purchase|dscr|short\s+term\s+rental|ground\s+up\s+construction|non\s+warrantable|condo|hoa\s+docs|litigation\s+docs|initial\s+cd|initial\s+le|payment\s+required|hit\s+list|withdraw|order\s+#|pricing\s+support\s+desk)\b/.test(
      subjLower,
    ) ||
    /\b(?:how\s+would\s+you\s+approach\s+this\s+scenario|scenario|10%\s+down|second\s+home|investment\s+properties|pre-?qual|prequal|dscr|ltv|cash\s+out|please\s+review|please\s+see\s+attached|new\s+document\s+has\s+been\s+uploaded|underwriting\s+condition|waive\s+the\s+condition|removed\s+on\s+monday|income\s+calculation|start\s+le\s+prep|re-sent\s+e-consent|seller\s+counter\s+offer|for\s+recording|need\s+to\s+close|hoa\s+docs)\b/.test(
      topBodyLower,
    ) ||
    /\b(?:statement|airdna|pre-?qual|prequal|1004|appraisal|invoice|insurance|uad|counter\s+offer|mortgagee)\b/.test(
      attsLower,
    );

  const actionability: EmailTriageMetadata["actionability"] =
    isAutomatedDigest || isShortAckReply
      ? "information_only"
      : hasActionSignal
        ? "potential_action"
        : subjLower.length > 0
          ? "information_only"
          : "unclear";

  return {
    subject,
    from,
    to,
    attachment_filenames,
    has_current_document_attachment: hasDocAttachment,
    actionability,
    email_model_checkpoint: LAYA_ARCHIVE_C5_EMAIL_CHECKPOINT,
  };
}

export type ConditionTargetFamily =
  | "hazard_flood_insurance"
  | "flood_insurance"
  | "property_tax"
  | "title_prelim_cpl"
  | "appraisal_valuation"
  | "purchase_contract"
  | "entity_llc"
  | "application_1003"
  | "paystub_voe"
  | "tax_form_w2_1099"
  | "tax_return_cpa_pnl"
  | "lease_rent_str"
  | "bank_asset"
  | "credit_bureau_report"
  | "mortgage_payoff"
  | "settlement_cd"
  | "loe_stip";

function extractConditionHeaderPrefix(clower: string): string {
  const trimmed = clower.replace(/^[0-9.):\-\s*«]+/, "");
  const dashIdx = trimmed.indexOf(" - ");
  const colonIdx = trimmed.indexOf(": ");
  let end = Math.min(trimmed.length, 55);
  if (dashIdx !== -1 && (colonIdx === -1 || dashIdx < colonIdx)) {
    end = dashIdx;
  } else if (colonIdx !== -1) {
    end = colonIdx;
  }
  return trimmed.slice(0, end).trim();
}

export function inferConditionTargetTags(rawCond: string): ConditionTargetFamily[] {
  const clower = (rawCond || "").toLowerCase();
  const headerPrefix = extractConditionHeaderPrefix(clower);

  // Administrative, ratio, expiration, or internal review rows do not auto-match document uploads
  if (
    headerPrefix.startsWith("dti") ||
    headerPrefix.startsWith("dscr ratio") ||
    headerPrefix.startsWith("seller concession") ||
    headerPrefix.startsWith("loan approval expiration") ||
    headerPrefix.startsWith("bridge cross default") ||
    headerPrefix.startsWith("lmc legal team") ||
    headerPrefix.startsWith("confirm buyer agent commission") ||
    headerPrefix.startsWith("vvoe") ||
    headerPrefix.startsWith("woe") ||
    headerPrefix.startsWith("appraisal review") ||
    headerPrefix.startsWith("bridge property taxes and insurance")
  ) {
    return [];
  }

  // Header-first deterministic family resolution for "<Category> - <Details>" conditions
  if (
    /\b(?:loe|vor)\b/.test(headerPrefix) ||
    /(?:letter of explanation|aka affidavit|borrower identification|verification of rent|credit inquiries)/.test(
      headerPrefix,
    ) ||
    /(?:provide signed and dated loe|loe signed and dated|provide a signed and dated letter of explanation|provide signed and dated letter of explanation|letter of explanation for|social security card|ssn card|divorce decree|separation agreement)/.test(
      clower,
    )
  ) {
    return ["loe_stip"];
  }
  if (headerPrefix.startsWith("appraisal") || headerPrefix.startsWith("second appraisal")) {
    return ["appraisal_valuation"];
  }
  if (
    headerPrefix.startsWith("reo") ||
    headerPrefix.startsWith("mortgage statement") ||
    headerPrefix.startsWith("mortgage payoff") ||
    headerPrefix.startsWith("debts to be paid")
  ) {
    return ["mortgage_payoff"];
  }
  if (
    headerPrefix.startsWith("preliminary title") ||
    headerPrefix.startsWith("closing protection letter") ||
    headerPrefix.startsWith("title") ||
    headerPrefix.startsWith("cpl")
  ) {
    return ["title_prelim_cpl"];
  }
  if (headerPrefix.startsWith("flood insurance")) {
    return ["flood_insurance"];
  }
  if (
    headerPrefix.startsWith("hazard insurance") ||
    headerPrefix.startsWith("condo master insurance") ||
    headerPrefix.startsWith("condo ho-6") ||
    headerPrefix.startsWith("condo hoa")
  ) {
    return ["hazard_flood_insurance"];
  }
  if (headerPrefix.startsWith("property tax")) {
    return ["property_tax"];
  }
  if (headerPrefix.startsWith("purchase contract") || headerPrefix.startsWith("bridge listing")) {
    return ["purchase_contract"];
  }
  if (headerPrefix.startsWith("llc documentation") || headerPrefix.startsWith("entity")) {
    return ["entity_llc"];
  }
  if (headerPrefix.startsWith("urla") || headerPrefix.startsWith("1003")) {
    return ["application_1003"];
  }
  if (
    (headerPrefix.startsWith("large deposit") && clower.includes("settlement statement")) ||
    headerPrefix.startsWith("bridge final cd") ||
    headerPrefix.startsWith("closing disclosure") ||
    headerPrefix.startsWith("settlement statement")
  ) {
    return ["settlement_cd"];
  }
  if (
    headerPrefix.startsWith("asset statement") ||
    headerPrefix.startsWith("assets") ||
    headerPrefix.startsWith("earnest money") ||
    headerPrefix.startsWith("deposit sourcing") ||
    headerPrefix.startsWith("cash/funds to close") ||
    headerPrefix.startsWith("vod")
  ) {
    return ["bank_asset"];
  }
  if (
    headerPrefix.startsWith("paystub") ||
    headerPrefix.startsWith("verification - provide written confirmation")
  ) {
    return ["paystub_voe"];
  }
  if (
    headerPrefix.includes("w2") ||
    headerPrefix.includes("w-2") ||
    (headerPrefix.startsWith("income") && /(?:w-2|w2|1099)/.test(clower))
  ) {
    return ["tax_form_w2_1099"];
  }
  if (
    headerPrefix.startsWith("self-employment") ||
    headerPrefix.startsWith("tax transcripts") ||
    (headerPrefix.startsWith("income") &&
      /(?:profit & loss|profit and loss|p&l|1040|schedule c)/.test(clower))
  ) {
    return ["tax_return_cpa_pnl"];
  }

  const tags: ConditionTargetFamily[] = [];
  const hasLoeCue =
    /\b(?:loe|vor)\b/.test(clower) ||
    /(?:letter of explanation|written explanation|letter explaining|explanation for|aka affidavit|occupancy certification|occupancy letter|gift letter|borrower identification|picture id|photo id|driver's license|drivers license|passport|verification of rent|non-occupant co-borrower)/.test(
      clower,
    );
  if (hasLoeCue) tags.push("loe_stip");
  if (
    /(?:hazard insurance|homeowner's insurance|homeowners insurance|declaration page|declarations page|dwelling fire|master insurance|ho-6|ho6|insurance policy|evidence of insurance|certificate of liability insurance|windstorm|builder's risk|builders risk)/.test(
      clower,
    )
  ) {
    tags.push("hazard_flood_insurance");
  }
  if (/(?:flood insurance|flood cert|flood zone)/.test(clower)) {
    tags.push("flood_insurance");
  }
  if (/(?:property tax|tax cert|tax bill|secured property tax|supplemental tax)/.test(clower)) {
    tags.push("property_tax");
  }
  if (
    /(?:preliminary title|title commitment|title report|closing protection letter|cpl|prelim|alta|wire instruction|escrow instruction)/.test(
      clower,
    )
  ) {
    tags.push("title_prelim_cpl");
  }
  if (
    /(?:appraisal|1004|1025|1073|1007|comparable rent schedule|secondary valuation|cda|bpo|final inspection|1004d|reconsideration of value)/.test(
      clower,
    )
  ) {
    tags.push("appraisal_valuation");
  }
  if (
    /(?:purchase contract|purchase agreement|sales contract|addendum|counter offer|earnest money|emd)/.test(
      clower,
    )
  ) {
    tags.push("purchase_contract");
  }
  if (
    /(?:llc documentation|operating agreement|articles of organization|certificate of formation|certificate of good standing|certificate of fact|ein letter|ss-4|business license|corporate resolution|entity documentation|partnership agreement)/.test(
      clower,
    )
  ) {
    tags.push("entity_llc");
  }
  if (/(?:urla|1003|loan application|self-employed questionnaire|\bseq\b)/.test(clower)) {
    tags.push("application_1003");
  }
  if (
    /(?:paystub|pay stub|earnings statement|written voe|verification of employment|\bvoe\b)/.test(
      clower,
    )
  ) {
    tags.push("paystub_voe");
  }
  if (/(?:w-2|\bw2\b|1099|k-1|\bk1\b|wage and tax)/.test(clower)) {
    tags.push("tax_form_w2_1099");
  }
  if (
    /(?:1040|1120|1065|tax return|tax transcript|profit & loss|profit and loss|p&l|cpa letter|schedule c|schedule e|preparer letter)/.test(
      clower,
    )
  ) {
    tags.push("tax_return_cpa_pnl");
  }
  if (
    /(?:lease agreement|rental agreement|rent roll|short-term rental|short term rental|airdna|vacation rental|str ledger)/.test(
      clower,
    )
  ) {
    tags.push("lease_rent_str");
  }
  if (
    /(?:asset statement|bank statement|brokerage statement|months statements|months of statements|account statement|checking account|savings account|verification of deposit|\bvod\b|funds to close|cash to close| earnest money receipt|cancelled check)/.test(
      clower,
    ) ||
    (clower.includes("statements for") &&
      /(?:account|accounts|ending in|vanguard|fidelity|schwab|chase|wells fargo|bank)/.test(clower))
  ) {
    tags.push("bank_asset");
  }
  if (
    /(?:credit supplement|updated credit report|tri-merge|trimerge|credit report)/.test(clower) &&
    !hasLoeCue &&
    !clower.includes("credit inquiries")
  ) {
    tags.push("credit_bureau_report");
  }
  if (
    /(?:mortgage statement|mtg stmt|payoff statement|payoff demand|loan payoff|beneficiary demand|reo )/.test(
      clower,
    )
  ) {
    tags.push("mortgage_payoff");
  }
  if (/(?:closing disclosure|settlement statement|loan estimate|hud-1|alta settlement)/.test(clower)) {
    tags.push("settlement_cd");
  }
  return tags;
}

export function inferDocumentTargetTags(
  docCategory: string,
  layaFamily: string,
  docTypeLabel: string,
  hay: string,
): ConditionTargetFamily[] {
  const cat = (docCategory || "").toLowerCase();
  const fam = (layaFamily || "").toLowerCase();
  const label = (docTypeLabel || "").toLowerCase();
  const h = (hay || "").toLowerCase();
  const tags: ConditionTargetFamily[] = [];

  if (cat === "assets" || fam === "bank_statement" || fam === "asset_verification") {
    tags.push("bank_asset");
    return tags;
  }
  if (cat === "income") {
    if (fam === "bank_statement" || label.includes("bank statement")) {
      tags.push("bank_asset");
    } else if (fam === "paystub" || fam === "w2_1099_paystub" && /(?:paystub|pay stub|earnings statement|gross pay|net pay|\bvoe\b)/.test(`${label} ${h}`)) {
      tags.push("paystub_voe");
    } else if (fam === "tax_form" || fam === "w2_1099_paystub" || label.includes("w-2") || label.includes("1099")) {
      tags.push("tax_form_w2_1099");
    } else if (fam === "lease" || fam === "lease_rent_roll" || label.includes("lease") || label.includes("rent roll")) {
      tags.push("lease_rent_str");
    } else {
      tags.push("tax_return_cpa_pnl");
    }
    return tags;
  }
  if (cat === "credit" || fam === "credit_report" || fam === "mortgage_statement") {
    if (
      fam === "mortgage_statement" ||
      label.includes("mortgage") ||
      label.includes("payoff") ||
      /(?:mortgage statement|payoff statement|payoff demand|beneficiary demand)/.test(h)
    ) {
      tags.push("mortgage_payoff");
    } else {
      tags.push("credit_bureau_report");
    }
    return tags;
  }
  if (cat === "property" || fam === "appraisal_valuation" || fam === "purchase_contract" || fam === "appraisal") {
    if (
      /(?:property tax|secured property tax|annual secured bills|tax collector|tax cert)/.test(h) &&
      !h.includes("uniform residential appraisal")
    ) {
      tags.push("property_tax");
    } else if (
      fam === "purchase_contract" ||
      label.includes("purchase") ||
      label.includes("contract") ||
      /(?:purchase agreement|purchase contract|counter offer|addendum|sales contract)/.test(h)
    ) {
      tags.push("purchase_contract");
    } else {
      tags.push("appraisal_valuation");
    }
    return tags;
  }
  if (cat === "title_insurance" || fam === "hazard_insurance" || fam === "title_commitment" || fam === "insurance") {
    if (fam === "insurance" || fam === "hazard_insurance") {
      if (
        /(?:flood certificate|flood hazard determination|flood insurance|flood cert)/.test(h) &&
        !/(?:homeowner|dwelling fire|hazard insurance declaration)/.test(h)
      ) {
        tags.push("flood_insurance");
      } else {
        tags.push("hazard_flood_insurance");
      }
    } else if (
      fam === "entity_formation" ||
      /(?:operating agreement|articles of organization|certificate of formation|certificate of good standing|certificate of fact|employer identification number|ein letter|ss-4|llc|entity document)/.test(
        h,
      )
    ) {
      tags.push("entity_llc");
    } else {
      tags.push("title_prelim_cpl");
    }
    return tags;
  }
  if (cat === "application" || fam === "urla_1003" || fam === "application") {
    tags.push("application_1003");
    return tags;
  }
  if (cat === "disclosures") {
    if (/(?:payoff statement|payoff demand)/.test(h)) {
      tags.push("mortgage_payoff");
    } else {
      tags.push("settlement_cd");
    }
    return tags;
  }
  if (cat === "conditions" || fam === "condition_stip_loe" || fam === "entity_formation") {
    if (
      fam === "entity_formation" ||
      /(?:operating agreement|articles of organization|certificate of formation|certificate of good standing|ein letter)/.test(
        h,
      )
    ) {
      tags.push("entity_llc");
    } else if (
      !/(?:conditional loan approval|conditional approval|conditions of approval)/.test(h)
    ) {
      tags.push("loe_stip");
    }
  }
  return tags;
}

const CONDITION_MATCH_STOPWORDS = new Set([
  "provide",
  "provided",
  "updated",
  "signed",
  "dated",
  "copy",
  "copies",
  "most",
  "recent",
  "showing",
  "borrower",
  "borrowers",
  "loan",
  "lendsure",
  "condition",
  "conditions",
  "prior",
  "from",
  "with",
  "that",
  "this",
  "must",
  "will",
  "have",
  "been",
  "page",
  "pages",
  "statement",
  "statements",
  "account",
  "accounts",
  "document",
  "documents",
  "documentation",
  "verification",
  "letter",
  "please",
  "upload",
  "review",
  "complete",
  "completed",
  "fully",
  "current",
  "valid",
  "all",
  "any",
  "for",
  "and",
  "the",
]);

const CONDITION_BANK_INSTITUTIONS = [
  "vanguard",
  "fidelity",
  "schwab",
  "empower",
  "texas partners",
  "wells fargo",
  "chase",
  "bank of america",
  "citibank",
  "us bank",
  "pnc",
  "capital one",
  "td bank",
  "navy federal",
  "usaa",
  "mercury",
  "relay",
  "venmo",
  "paypal",
  "inbursa",
  "verabank",
  "frost bank",
  "huntington",
  "regions",
  "truist",
  "citizens bank",
  "bmo",
  "keybank",
  "ally bank",
  "discover",
  "morgan stanley",
  "merrill",
  "edward jones",
  "ubs",
  "thrift savings",
  "national bank",
  "pacificpremier",
  "pacific premier",
  "first republic",
  "allegacy",
  "bank of the sierra",
  "noble bank",
];

/**
 * Deterministic 15-family semantic condition matcher mirroring `match_open_condition` in `smart_drop.rs`.
 * Returns the best-matching `condition_id` from `openConditions`, or `null` if no open condition matches
 * the document's semantic target family.
 */
export function matchOpenCondition(
  docCategory: string,
  layaFamily: string,
  docTypeLabel: string,
  fileName: string,
  textExcerpt: string | undefined,
  openConditions: ReadonlyArray<{ condition_id: string; text: string }>,
): string | null {
  if (!openConditions || openConditions.length === 0) return null;
  const hay = `${fileName || ""} ${textExcerpt || ""}`.toLowerCase();
  const docTags = inferDocumentTargetTags(docCategory, layaFamily, docTypeLabel, hay);
  if (docTags.length === 0) return null;

  const acctRegex = /\b\d{4}\b/g;
  const docNumbers = new Set<string>(hay.match(acctRegex) ?? []);

  let bestMatch: { id: string; score: number } | null = null;
  for (const cond of openConditions) {
    const clower = (cond.text || "").toLowerCase();
    const condTags = inferConditionTargetTags(clower);
    if (!docTags.some((dt) => condTags.includes(dt))) {
      continue;
    }

    let score = 100;

    // 1. Account last-4 digit specificity
    const condNumbers = Array.from(new Set(clower.match(acctRegex) ?? [])).filter(
      (n) => !n.startsWith("202") && n !== "1003" && n !== "1004" && n !== "1040" && n !== "1099",
    );
    if (condNumbers.length > 0) {
      const matchedNums = condNumbers.filter((n) => docNumbers.has(n)).length;
      if (matchedNums > 0) {
        score += matchedNums * 50;
      } else {
        score -= 25;
      }
    }

    // 2. Institution name specificity (and bank_asset institution guard)
    let condHasAnyBankInst = false;
    let docMatchesBankInst = false;
    for (const inst of CONDITION_BANK_INSTITUTIONS) {
      if (clower.includes(inst)) {
        condHasAnyBankInst = true;
        if (hay.includes(inst)) {
          docMatchesBankInst = true;
          score += 40;
        }
      }
    }
    if (docTags.includes("bank_asset") && condHasAnyBankInst && !docMatchesBankInst) {
      continue;
    }

    // 3. Payoff demand vs regular mortgage statement disambiguation
    if (docTags.includes("mortgage_payoff")) {
      const condIsPayoff = clower.includes("payoff");
      const docIsPayoff = hay.includes("payoff");
      if (condIsPayoff && docIsPayoff) {
        score += 40;
      } else if (condIsPayoff !== docIsPayoff) {
        score -= 30;
      }
    }

    // 4. Meaningful token overlap tie-breaker
    const words = Array.from(
      new Set(
        clower
          .split(/[^a-z0-9]+/)
          .filter((w) => w.length >= 4 && !CONDITION_MATCH_STOPWORDS.has(w)),
      ),
    );
    let shared = 0;
    for (const w of words) {
      if (hay.includes(w)) shared += 1;
    }
    score += shared * 6;

    if (!bestMatch || score > bestMatch.score) {
      bestMatch = { id: cond.condition_id, score };
    }
  }

  return bestMatch ? bestMatch.id : null;
}

/**
 * Extracts Laya's 16-class document family, statement period, and `mixed_page` segmentation guard
 * while enforcing `authority: "advisory_unreviewed"`, `originals_retained: true`, `lending_decision: "not_made"`.
 */
export function getLayaHighlights(
  highlights: Record<string, unknown> | null | undefined,
): LayaExtractedHighlights {
  const h = highlights ?? {};
  const laya_family =
    typeof h.laya_family === "string" && h.laya_family.trim().length > 0
      ? h.laya_family.trim()
      : null;
  const secondary_laya_families = Array.isArray(h.secondary_laya_families)
    ? h.secondary_laya_families.filter((f): f is string => typeof f === "string")
    : undefined;
  const is_multi_document_bundle =
    typeof h.is_multi_document_bundle === "boolean"
      ? h.is_multi_document_bundle
      : secondary_laya_families !== undefined
        ? secondary_laya_families.length > 0
        : undefined;
  let statement_period: string | null = null;
  if (typeof h.statement_period === "string" && h.statement_period.trim().length > 0) {
    statement_period = h.statement_period.trim();
  } else if (h.statement_period && typeof h.statement_period === "object") {
    const pObj = h.statement_period as {
      status?: string;
      start?: string;
      end?: string;
    };
    if (pObj.status === "source_extracted" && pObj.start && pObj.end) {
      statement_period = `${pObj.start} to ${pObj.end}`;
    } else if (typeof pObj.start === "string" && pObj.start.trim().length > 0) {
      statement_period = pObj.start.trim();
    }
  }
  const segmentation_guard_triggered = Boolean(
    h.segmentation_guard_triggered === true || laya_family === "mixed_page",
  );
  const model_checkpoint =
    typeof h.model_checkpoint === "string" && h.model_checkpoint.trim().length > 0
      ? h.model_checkpoint.trim()
      : undefined;
  const structural_features = Array.isArray(h.structural_features)
    ? h.structural_features.filter((f): f is string => typeof f === "string")
    : undefined;
  const email_triage =
    h.email_triage && typeof h.email_triage === "object"
      ? (h.email_triage as EmailTriageMetadata)
      : undefined;
  const document_completeness =
    h.document_completeness && typeof h.document_completeness === "object"
      ? (h.document_completeness as DocumentCompletenessDiagnostics)
      : undefined;
  const intake_warnings = Array.isArray(h.intake_warnings)
    ? (h.intake_warnings as DocumentCompletenessWarning[])
    : undefined;

  return {
    ...h,
    laya_family,
    ...(secondary_laya_families ? { secondary_laya_families } : {}),
    ...(is_multi_document_bundle !== undefined ? { is_multi_document_bundle } : {}),
    statement_period,
    segmentation_guard_triggered,
    ...(model_checkpoint ? { model_checkpoint } : {}),
    ...(structural_features ? { structural_features } : {}),
    ...(email_triage ? { email_triage } : {}),
    ...(document_completeness ? { document_completeness } : {}),
    ...(intake_warnings ? { intake_warnings } : {}),
    authority: "advisory_unreviewed",
    originals_retained: true,
    lending_decision: "not_made",
  };
}

export const smartDropKeys = {
  all: ["smart-drop"] as const,
  links: (params?: { person_id?: string; loan_id?: string }) =>
    ["smart-drop", "links", params ?? {}] as const,
  inbox: (params?: {
    routed_seat?: string;
    person_id?: string;
    loan_id?: string;
    review_status?: string;
  }) => ["smart-drop", "inbox", params ?? {}] as const,
  publicPortal: (token: string) => ["smart-drop", "public", token] as const,
};

/** Build the shareable zero-login Smart Drop URL (`/drop/:token`). */
export function buildSmartDropUrl(token: string, origin?: string): string {
  const baseOrigin =
    origin ??
    (typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "http://localhost:5173");
  return `${baseOrigin.replace(/\/+$/, "")}/drop/${encodeURIComponent(token.trim())}`;
}

/** Build the Co-Branded Retail / LO Point-of-Sale URL (`/pos/:token`). */
export function buildPosUrl(token: string, origin?: string): string {
  const baseOrigin =
    origin ??
    (typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "http://localhost:5173");
  return `${baseOrigin.replace(/\/+$/, "")}/pos/${encodeURIComponent(token.trim())}`;
}

/** Build the First-Party Native LS2 e-Signature Studio URL (`/sign/:token`). */
export function buildNativeSignUrl(token: string, origin?: string): string {
  const baseOrigin =
    origin ??
    (typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "http://localhost:5173");
  return `${baseOrigin.replace(/\/+$/, "")}/sign/${encodeURIComponent(token.trim())}`;
}

export function formatSmartDropBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const DOC_STOP_TOKENS = new Set([
  "1003",
  "1004",
  "1007",
  "1025",
  "1040",
  "1065",
  "1099",
  "1120",
  "1120s",
  "urla",
  "mismo",
  "fnm",
  "application",
  "app",
  "loan",
  "bank",
  "statement",
  "statements",
  "stmt",
  "stmts",
  "checking",
  "savings",
  "asset",
  "assets",
  "vod",
  "vome",
  "reserves",
  "brokerage",
  "account",
  "credit",
  "trimerge",
  "tri",
  "merge",
  "fico",
  "cbr",
  "report",
  "inquiry",
  "loe",
  "explanation",
  "derog",
  "bankruptcy",
  "bk",
  "foreclosure",
  "payoff",
  "mortgage",
  "lates",
  "income",
  "tax",
  "return",
  "returns",
  "pnl",
  "profit",
  "loss",
  "cpa",
  "letter",
  "paystub",
  "paystubs",
  "w2",
  "w2s",
  "k1",
  "voe",
  "lease",
  "leases",
  "rent",
  "roll",
  "schedule",
  "str",
  "airbnb",
  "vrbo",
  "dscr",
  "cashflow",
  "appraisal",
  "bpo",
  "cda",
  "arv",
  "rehab",
  "budget",
  "purchase",
  "contract",
  "psa",
  "escrow",
  "earnest",
  "emd",
  "property",
  "photos",
  "inspection",
  "title",
  "prelim",
  "preliminary",
  "commitment",
  "cpl",
  "wire",
  "hazard",
  "hoi",
  "insurance",
  "flood",
  "binder",
  "eo",
  "policy",
  "survey",
  "operating",
  "agreement",
  "articles",
  "organization",
  "incorporation",
  "ein",
  "irs",
  "bylaws",
  "certificate",
  "good",
  "standing",
  "resolution",
  "stip",
  "stips",
  "condition",
  "conditions",
  "ptd",
  "ptf",
  "ptc",
  "ptp",
  "cleared",
  "clearing",
  "updated",
  "disclosure",
  "disclosures",
  "le",
  "cd",
  "intent",
  "proceed",
  "econsent",
  "signed",
  "unsigned",
  "package",
  "pkg",
  "doc",
  "docs",
  "document",
  "documents",
  "file",
  "scan",
  "upload",
  "draft",
  "final",
  "copy",
  "scenario",
  "prequal",
  "mixed",
  "combined",
  "bundle",
  "airdna",
  "rentalizer",
  "overview",
  "review",
  "pq",
  "guide",
  "completed",
  "adm",
  "tech",
  "initial",
  "estmt",
]);

const ENTITY_WORDS = new Set([
  "llc",
  "inc",
  "corp",
  "corporation",
  "holdings",
  "properties",
  "investments",
  "capital",
  "ventures",
  "partners",
  "group",
  "realty",
  "enterprises",
  "residences",
  "homes",
  "rentals",
  "management",
  "trust",
  "lp",
  "llp",
  "associates",
  "fund",
  "development",
]);

const COMMON_GIVEN_NAMES = new Set([
  "elena",
  "david",
  "priya",
  "michael",
  "sarah",
  "john",
  "james",
  "robert",
  "maria",
  "carlos",
  "sofia",
  "marcus",
  "rachel",
  "daniel",
  "emily",
  "jessica",
  "ashley",
  "amanda",
  "melissa",
  "nicole",
  "elizabeth",
  "laura",
  "rebecca",
  "stephanie",
  "jennifer",
  "lisa",
  "angela",
  "kimberly",
  "michelle",
  "tiffany",
  "christina",
  "natalie",
  "victoria",
  "hannah",
  "samantha",
  "alex",
  "alexander",
  "andrew",
  "anthony",
  "benjamin",
  "brandon",
  "brian",
  "charles",
  "christopher",
  "eric",
  "jason",
  "jeffrey",
  "jonathan",
  "joseph",
  "joshua",
  "justin",
  "kevin",
  "matthew",
  "nathan",
  "nicholas",
  "patrick",
  "paul",
  "richard",
  "ryan",
  "samuel",
  "scott",
  "stephen",
  "steven",
  "timothy",
  "tyler",
  "william",
  "zachary",
  "grace",
  "chloe",
  "zoe",
  "lily",
  "maya",
  "nora",
  "eva",
  "anna",
  "claire",
  "alice",
  "julian",
  "lucas",
  "mateo",
  "leo",
  "omar",
  "tariq",
  "wei",
  "mei",
  "raj",
  "arjun",
  "ananya",
  "liam",
  "noah",
  "oliver",
  "elijah",
  "ethan",
  "jacob",
  "aiden",
  "kenji",
  "hiroshi",
  "fatima",
  "amina",
]);

function titleCaseToken(token: string): string {
  const lower = token.toLowerCase();
  if (
    lower === "llc" ||
    lower === "llp" ||
    lower === "lp" ||
    lower === "inc" ||
    lower === "corp" ||
    lower === "pllc" ||
    lower === "ii" ||
    lower === "iii" ||
    lower === "iv"
  ) {
    return lower.toUpperCase();
  }
  if (lower.length === 0) return "";
  if (token.length === 2 && (token.endsWith(".") || /^[A-Z]{2}$/.test(token))) {
    return token.toUpperCase();
  }
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

const BAD_BORROWER_SUBSTRINGS = [
  "mortgage",
  "lending",
  "bank",
  "credit union",
  "fargo",
  "chase",
  "citibank",
  "fidelity",
  "vanguard",
  "schwab",
  "title",
  "escrow",
  "insurance",
  "appraisal",
  "statement",
  "summary",
  "balance",
  "account",
  "checking",
  "savings",
  "deposit",
  "withdrawal",
  "payment",
  "transaction",
  "schedule",
  "page ",
  "page number",
  "form ",
  "internal revenue",
  "department of",
  "united states",
  "social security",
  "taxpayer",
  "identification",
  "counter offer",
  "response to",
  "financial advisor",
  "advisor",
  "file no",
  "newrez",
  "shellpoint",
  "pennymac",
  "carrington",
  "cenlar",
  "loancare",
  "signature",
  "authorization",
  "certification",
  "acknowledgment",
  "acknowledgement",
  "consent",
  "limitations",
  "comparison",
  "system",
  "customer service",
  "member fdic",
  "equal housing",
  "po box",
  "p.o. box",
  "date of birth",
  "redacted",
  "specimen",
  "placeholder",
  "removed",
  "tech pq report",
  "ae pq report",
  "preliminary loan terms",
];

function cleanExtractedBorrowerOrEntity(raw: string): string {
  let s = (raw.split(/[|;\r\n]/)[0] ?? "").trim();
  if (/e\s*x\s*t\s*r\s*eme\s+c\s*l\s*e\s*a\s*n\s*i\s*n\s*g/i.test(s)) {
    return "Extreme Cleaning Solutions LLC";
  }
  const presortIdx = s.toLowerCase().lastIndexOf("nnnnn");
  if (presortIdx !== -1) {
    const afterN = s.slice(presortIdx).replace(/^[nN\s]+/, "");
    if (afterN) s = afterN;
  }
  s = s.replace(/\s*\((?:ira|tod|roth[^)]*|sep[^)]*|individual|joint|demo|b20[^)]*)\)\s*$/i, "");
  for (const suffixTag of [
    "owner of public record",
    "borrower email",
    "lo email",
    "nbs names",
    "nbs emails",
    "business start",
    "business name",
    "borrower %",
    "employee address",
    "payroll relationship",
    "your social security",
    "social security number",
    "account number",
    "property address",
    "subject property",
    "co-applicant",
    "co-borrower",
    "— fico",
    "– fico",
    "- fico",
    "fico:",
    ", trustees",
    ", trustee",
    ", co-trustee",
    " as trustee",
    " dated ",
  ]) {
    const idx = s.toLowerCase().indexOf(suffixTag);
    if (idx !== -1) {
      s = s.slice(0, idx);
    }
  }
  const llcComma = s.toLowerCase().indexOf("llc,");
  if (llcComma !== -1) {
    s = s.slice(0, llcComma + 3);
  }
  for (const trail of [
    /\b(?:loan\s*(?:number|no\.?|#)|lender\s*loan|case\s*(?:number|no\.?|#)|file\s*(?:number|no\.?|#)|property\s*address|subject\s*property|co-borrower|account\s*number|ssn|date)\s*[:#].*$/i,
  ]) {
    s = s.replace(trail, "");
  }
  s = s
    .replace(/^[,.;:|_\-—–*()[\]"'~\s]+|[,.;:|_\-—–*()[\]"'~\s]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (/^(?:bl|oi)\s+/i.test(s) && s.length > 4) {
    s = s.slice(3).trim();
  } else if (/^(?:and|for)\s+/i.test(s) && s.length > 5) {
    s = s.slice(4).trim();
  }
  if (/,\s*llc$/i.test(s)) {
    s = `${s.replace(/,\s*llc$/i, "").trim()} LLC`;
  }
  if (s.includes("/")) {
    const firstSeg = s.split("/")[0]?.trim();
    if (firstSeg) s = firstSeg;
  }
  if (s.includes(",") && !/\b(?:llc|inc|corp|lp|llp)\b/i.test(s)) {
    const parts = s
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    if (
      parts.length === 2 &&
      parts[0]!.split(/\s+/).length <= 2 &&
      parts[1]!.split(/\s+/).length <= 2
    ) {
      s = `${parts[1]} ${parts[0]}`;
    }
  }
  s = s
    .replace(/WHITFOR!\s*D/g, "WHITFORD")
    .replace(/Whitfordand\s+/g, "Whitford and ")
    .replace(/JoThornb/g, "Jo Thornby")
    .replace(/Thornb\.?$/g, "Thornby");

  if (s && s === s.toUpperCase() && /[A-Z]/.test(s)) {
    return s.split(/\s+/).map(titleCaseToken).join(" ");
  }
  return s;
}

function isValidBorrowerOrEntityCandidate(cand: string): boolean {
  const trimmed = cand.trim();
  if (trimmed.length < 4 || trimmed.length > 75) return false;
  const low = trimmed.toLowerCase();
  const isNumberedEntity =
    low.endsWith(" llc") || low.includes("llc ") || low.endsWith(" land trust");
  if (/\d/.test(trimmed) && !isNumberedEntity) return false;
  if (!/[A-Z]/.test(trimmed)) return false;
  if (
    [
      "none",
      "n/a",
      "na",
      "tbd",
      "unknown",
      "borrower",
      "co-borrower",
      "applicant",
      "co-applicant",
      "buyer",
      "seller",
      "owner",
      "tenant",
      "landlord",
      "client",
      "total",
      "subtotal",
      "purchase",
      "refinance",
    ].includes(low)
  ) {
    return false;
  }
  if (
    BAD_BORROWER_SUBSTRINGS.some((b) => low.includes(b)) &&
    !(low.endsWith(" llc") && low.includes("bayou lantern"))
  ) {
    return false;
  }
  const words = trimmed.split(/[\s,&.]+/).filter(Boolean);
  if (words.length < 2 || words.length > 7) return false;
  if (!words.some((w) => w.length >= 3)) return false;
  return true;
}

function extractXmlTagInScope(scope: string, tag: string): string | null {
  const re = new RegExp(
    `<(?:[a-zA-Z0-9_]+:)?${tag}>\\s*([^<\\r\\n]+?)\\s*<\\/(?:[a-zA-Z0-9_]+:)?${tag}>`,
    "i",
  );
  const m = re.exec(scope);
  return m?.[1]?.trim() ? m[1].trim() : null;
}

function extractXmlAttrInTag(tagText: string, attr: string): string | null {
  const re = new RegExp(`\\b${attr}\\s*=\\s*"([^"]+)"`, "i");
  const m = re.exec(tagText);
  return m?.[1]?.trim() ? m[1].trim() : null;
}

function extractNameFromXmlScope(scope: string): string | null {
  const full =
    extractXmlTagInScope(scope, "FullName") ?? extractXmlTagInScope(scope, "UnparsedName");
  if (full) {
    const cleaned = cleanExtractedBorrowerOrEntity(full);
    if (isValidBorrowerOrEntityCandidate(cleaned)) return cleaned;
  }
  const first = extractXmlTagInScope(scope, "FirstName");
  const middle = extractXmlTagInScope(scope, "MiddleName");
  const last = extractXmlTagInScope(scope, "LastName");
  if (first && last) {
    const combined = middle?.trim()
      ? `${first.trim()} ${middle.trim()} ${last.trim()}`
      : `${first.trim()} ${last.trim()}`;
    const cleaned = cleanExtractedBorrowerOrEntity(combined);
    if (isValidBorrowerOrEntityCandidate(cleaned)) return cleaned;
  }
  const entity = extractXmlTagInScope(scope, "LegalEntityName");
  if (entity) {
    const cleaned = cleanExtractedBorrowerOrEntity(entity);
    if (isValidBorrowerOrEntityCandidate(cleaned)) return cleaned;
  }
  return null;
}

/**
 * Extracts borrower or vesting entity identity from MISMO 3.4 / 2.6 GSE XML or multi-line PDF/text layouts.
 */
export function extractDocumentBorrowerOrEntity(textExcerpt?: string | null): string | null {
  if (!textExcerpt || !textExcerpt.trim()) return null;

  // 1. MISMO 3.4 & 2.6 GSE XML: Prefer <PARTY> with Borrower role or <BORROWER>/<INDIVIDUAL>,
  //    stripping <ASSET_HOLDER> so bank <FullName> tags are never mistaken for the borrower.
  if (textExcerpt.includes("<")) {
    const partyRe = /<(?:[a-zA-Z0-9_]+:)?PARTY\b[^>]*>([\s\S]*?)<\/(?:[a-zA-Z0-9_]+:)?PARTY>/gi;
    let pm: RegExpExecArray | null;
    while ((pm = partyRe.exec(textExcerpt)) !== null) {
      const partyBlock = pm[1] ?? "";
      const role = extractXmlTagInScope(partyBlock, "PartyRoleType")?.toLowerCase() ?? "";
      const hasBorrowerRole =
        role === "borrower" ||
        role.includes("borrower") ||
        (!role && /<(?:[a-zA-Z0-9_]+:)?BORROWER\b/i.test(partyBlock));
      if (hasBorrowerRole) {
        const indivMatch =
          /<(?:[a-zA-Z0-9_]+:)?INDIVIDUAL\b[^>]*>([\s\S]*?)<\/(?:[a-zA-Z0-9_]+:)?INDIVIDUAL>/i.exec(
            partyBlock,
          );
        const extracted =
          extractNameFromXmlScope(indivMatch?.[1] ?? partyBlock) ??
          extractNameFromXmlScope(partyBlock);
        if (extracted) return extracted;
      }
    }

    const borrowerTagRe = /<(?:[a-zA-Z0-9_]+:)?BORROWER\b[^>]*>/gi;
    let btm: RegExpExecArray | null;
    while ((btm = borrowerTagRe.exec(textExcerpt)) !== null) {
      const tagStr = btm[0] ?? "";
      for (const attr of ["_UnparsedName", "UnparsedName", "GSEBorrowerName"]) {
        const val = extractXmlAttrInTag(tagStr, attr);
        if (val) {
          const cleaned = cleanExtractedBorrowerOrEntity(val);
          if (isValidBorrowerOrEntityCandidate(cleaned)) return cleaned;
        }
      }
      const f = extractXmlAttrInTag(tagStr, "_FirstName") ?? extractXmlAttrInTag(tagStr, "FirstName");
      const l = extractXmlAttrInTag(tagStr, "_LastName") ?? extractXmlAttrInTag(tagStr, "LastName");
      if (f && l) {
        const cleaned = cleanExtractedBorrowerOrEntity(`${f} ${l}`);
        if (isValidBorrowerOrEntityCandidate(cleaned)) return cleaned;
      }
    }

    for (const containerTag of ["BORROWER", "INDIVIDUAL"]) {
      const blockRe = new RegExp(
        `<(?:[a-zA-Z0-9_]+:)?${containerTag}\\b[^>]*>([\\s\\S]*?)<\\/(?:[a-zA-Z0-9_]+:)?${containerTag}>`,
        "gi",
      );
      let bm: RegExpExecArray | null;
      while ((bm = blockRe.exec(textExcerpt)) !== null) {
        const extracted = extractNameFromXmlScope(bm[1] ?? "");
        if (extracted) return extracted;
      }
    }

    const strippedXml = textExcerpt.replace(
      /<(?:[a-zA-Z0-9_]+:)?ASSET_HOLDER\b[^>]*>[\s\S]*?<\/(?:[a-zA-Z0-9_]+:)?ASSET_HOLDER>/gi,
      "",
    );
    const fallbackXml = extractNameFromXmlScope(strippedXml);
    if (fallbackXml) return fallbackXml;
  }

  const lines = textExcerpt
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return null;

  // 2. URLA 1003, LMC SEQ, Tech/AE PQ Report, Preliminary Loan Terms
  for (let i = 0; i < lines.length; i++) {
    const low = lines[i]!.toLowerCase();
    if (
      low.includes("(or individual taxpayer identification number)") ||
      low === "name (first, middle, last, suffix)"
    ) {
      if (i >= 1) {
        const prev = cleanExtractedBorrowerOrEntity(lines[i - 1]!);
        if (isValidBorrowerOrEntityCandidate(prev)) return prev;
      }
      for (let j = i + 1; j < Math.min(lines.length, i + 5); j++) {
        const cand = cleanExtractedBorrowerOrEntity(lines[j]!);
        if (isValidBorrowerOrEntityCandidate(cand)) return cand;
      }
    }
    if (low === "borrower & business identification") {
      for (let j = i + 1; j < Math.min(lines.length, i + 4); j++) {
        const cand = cleanExtractedBorrowerOrEntity(lines[j]!);
        if (isValidBorrowerOrEntityCandidate(cand)) return cand;
      }
    }
    for (const suffix of [" tech pq report", " ae pq report"]) {
      if (low.endsWith(suffix) && lines[i]!.length > suffix.length) {
        const cand = cleanExtractedBorrowerOrEntity(lines[i]!.slice(0, -suffix.length));
        if (isValidBorrowerOrEntityCandidate(cand)) return cand;
      }
    }
    if (low.startsWith("preliminary loan terms |")) {
      const right = lines[i]!.split("|")[1];
      if (right) {
        const cand = cleanExtractedBorrowerOrEntity(right);
        if (isValidBorrowerOrEntityCandidate(cand)) return cand;
      }
    }
  }

  // 3. Same-line explicit labels
  const sameLineLabels = [
    "borrower name (first, middle, last, suffix )",
    "borrower name (first, middle, last, suffix)",
    "borrower name(s):",
    "borrower names):",
    "borrower names:",
    "borrower name:",
    "purchaser/borrower:",
    "borrower/company:",
    "primary borrower —",
    "primary borrower –",
    "primary borrower -",
    "primary borrower:",
    "borrowers:",
    "borrower(s):",
    "borrower:",
    "applicant name:",
    "applicant(s):",
    "applicant:",
    "account holder(s):",
    "account holder:",
    "full account name:",
    "account name:",
    "simplified employee plan of",
    "schwab one® account of",
    "schwab one account of",
    "rollover ira of",
    "prepared for:",
    "client:",
    "entity name:",
    "llc name:",
    "company name:",
    "received from:",
    "buyer:",
    "named insured:",
    "insured:",
    "print name/date:",
    "landlords:",
    "landlord:",
  ];
  for (const line of lines) {
    const low = line.toLowerCase();
    for (const lbl of sameLineLabels) {
      const pos = low.indexOf(lbl);
      if (pos !== -1) {
        const after = line.slice(pos + lbl.length).trim();
        const cand = cleanExtractedBorrowerOrEntity(after);
        if (isValidBorrowerOrEntityCandidate(cand)) return cand;
      }
    }
  }

  // 4. Prefer "Full account name:" on brokerage statements before page-1 OCR'd "ACCOUNT NAME:"
  for (let i = 0; i < lines.length - 1; i++) {
    if (lines[i]!.toLowerCase() === "full account name:") {
      const cand = cleanExtractedBorrowerOrEntity(lines[i + 1]!);
      if (isValidBorrowerOrEntityCandidate(cand)) return cand;
    }
  }

  // 5. Next-line explicit labels (multi-column pdftotext layouts)
  const nextLineLabels = new Set([
    "borrower name(s):",
    "borrower name(s)",
    "borrower names)",
    "borrower names:",
    "borrower name:",
    "borrower name",
    "borrowers:",
    "borrower(s):",
    "borrower/company:",
    "borrower:",
    "borrower",
    "applicant(s):",
    "applicant:",
    "applicants",
    "full account name:",
    "account name:",
    "prepared for:",
    "client:",
    "entity name:",
    "llc name:",
    "company name:",
    "you as named insured and your address",
    "you as named insured",
    "name (first, middle, last, suffix)",
    "custorier name/proparty address:",
    "customer name/property address:",
    "manager or member name",
    "agent name",
    "attention",
    "landlords:",
    "landlord:",
    "print name/date:",
    "operating agreement of",
    "articles of organization of",
  ]);
  for (let i = 0; i < lines.length; i++) {
    const low = lines[i]!.replace(/^[|\s]+|[|\s]+$/g, "").toLowerCase();
    const isEmp =
      low.includes("employee's name, address") ||
      low.includes("emplovee name") ||
      low === "employee name";
    if (nextLineLabels.has(low) || isEmp) {
      if (low.includes("employee's name, address") && i + 2 < lines.length) {
        const comb = cleanExtractedBorrowerOrEntity(`${lines[i + 1]} ${lines[i + 2]}`);
        if (isValidBorrowerOrEntityCandidate(comb)) return comb;
      }
      for (let j = i + 1; j < Math.min(lines.length, i + 6); j++) {
        const nlLow = lines[j]!.toLowerCase();
        if (
          nlLow.startsWith("account number") ||
          nlLow.startsWith("your financial advisor") ||
          nlLow.startsWith("loan officer") ||
          nlLow.startsWith("co-borrower:") ||
          nlLow.startsWith("borrower ssn")
        ) {
          break;
        }
        const cand = cleanExtractedBorrowerOrEntity(lines[j]!);
        if (isValidBorrowerOrEntityCandidate(cand)) return cand;
      }
    }
  }

  // 6. PayPal Merchant Statement ("Statement for <Month> <Year>" -> next line) & JPMorgan Chase presort code
  for (let i = 0; i < Math.min(lines.length - 1, 25); i++) {
    const low = lines[i]!.toLowerCase();
    if (low.startsWith("statement for ")) {
      const cand = cleanExtractedBorrowerOrEntity(lines[i + 1]!);
      if (isValidBorrowerOrEntityCandidate(cand)) return cand;
    }
    if (/^\d{10,12}$/.test(lines[i]!) && lines[i]!.startsWith("00")) {
      const cand = cleanExtractedBorrowerOrEntity(lines[i + 1]!);
      if (isValidBorrowerOrEntityCandidate(cand)) return cand;
    }
  }

  // 7. Driver's License LN / FN pairing
  let dlLast: string | null = null;
  let dlFirst: string | null = null;
  for (let i = 0; i < Math.min(lines.length, 30); i++) {
    const low = lines[i]!.toLowerCase();
    if (low.startsWith("ln ") && lines[i]!.length > 3) {
      dlLast = lines[i]!.slice(3).trim();
    } else if (low.startsWith("fn ") && lines[i]!.length > 3) {
      dlFirst = lines[i]!.slice(3).trim();
    }
  }
  if (dlFirst && dlLast) {
    const cand = cleanExtractedBorrowerOrEntity(`${dlFirst} ${dlLast}`);
    if (isValidBorrowerOrEntityCandidate(cand)) return cand;
  }

  // 8. Entity Formation / Operating Agreement / Top-of-statement ALL-CAPS LLC
  for (let i = 0; i < lines.length; i++) {
    const low = lines[i]!.toLowerCase();
    if (
      low === "operating agreement" &&
      i + 2 < lines.length &&
      lines[i + 1]!.toLowerCase() === "of"
    ) {
      const cand = cleanExtractedBorrowerOrEntity(lines[i + 2]!);
      if (isValidBorrowerOrEntityCandidate(cand)) return cand;
    }
    if (low.includes("sole mbr") && i >= 1) {
      const cand = cleanExtractedBorrowerOrEntity(lines[i - 1]!);
      if (isValidBorrowerOrEntityCandidate(cand)) return cand;
    }
    if (
      i < 40 &&
      lines[i]!.length <= 45 &&
      lines[i]!.endsWith(" LLC") &&
      lines[i]! === lines[i]!.toUpperCase()
    ) {
      const cand = cleanExtractedBorrowerOrEntity(lines[i]!);
      if (isValidBorrowerOrEntityCandidate(cand)) return cand;
    }
  }

  return null;
}

/**
 * Extracts subject property address from hint, MISMO 3.4 `<SUBJECT_PROPERTY>`, MISMO 2.6 GSE `<PROPERTY>`, or PDF text labels.
 */
export function extractPropertyAddress(
  textExcerpt?: string | null,
  propertyHint?: string | null,
): string | null {
  if (propertyHint && propertyHint.trim()) return propertyHint.trim();
  if (!textExcerpt || !textExcerpt.trim()) return null;

  const formatXmlAddress = (scope: string): string | null => {
    const line = extractXmlTagInScope(scope, "AddressLineText");
    if (!line || line.trim().toLowerCase() === "tbd") return null;
    const unit = extractXmlTagInScope(scope, "AddressUnitIdentifier");
    const city = extractXmlTagInScope(scope, "CityName");
    const state = extractXmlTagInScope(scope, "StateCode");
    const zip = extractXmlTagInScope(scope, "PostalCode");
    const street = unit && !line.includes(unit) ? `${line} Unit ${unit}` : line;
    const parts = [street];
    if (city) parts.push(city);
    if (state) parts.push(zip ? `${state} ${zip}` : state);
    return parts.join(", ");
  };

  if (textExcerpt.includes("<")) {
    const spMatch =
      /<(?:[a-zA-Z0-9_]+:)?SUBJECT_PROPERTY\b[^>]*>([\s\S]*?)<\/(?:[a-zA-Z0-9_]+:)?SUBJECT_PROPERTY>/i.exec(
        textExcerpt,
      );
    if (spMatch?.[1]) {
      const addr = formatXmlAddress(spMatch[1]);
      if (addr) return addr;
    }
    const propTagRe = /<(?:[a-zA-Z0-9_]+:)?PROPERTY\b[^>]*>/gi;
    let ptm: RegExpExecArray | null;
    while ((ptm = propTagRe.exec(textExcerpt)) !== null) {
      const tagStr = ptm[0] ?? "";
      const street =
        extractXmlAttrInTag(tagStr, "_StreetAddress") ??
        extractXmlAttrInTag(tagStr, "StreetAddress");
      if (street && street.toLowerCase() !== "tbd") {
        const city = extractXmlAttrInTag(tagStr, "_City") ?? extractXmlAttrInTag(tagStr, "City");
        const state = extractXmlAttrInTag(tagStr, "_State") ?? extractXmlAttrInTag(tagStr, "State");
        const zip =
          extractXmlAttrInTag(tagStr, "_PostalCode") ?? extractXmlAttrInTag(tagStr, "PostalCode");
        const parts = [street];
        if (city) parts.push(city);
        if (state) parts.push(zip ? `${state} ${zip}` : state);
        return parts.join(", ");
      }
    }
    const addrRe =
      /<(?:[a-zA-Z0-9_]+:)?ADDRESS\b[^>]*>([\s\S]*?)<\/(?:[a-zA-Z0-9_]+:)?ADDRESS>/gi;
    let am: RegExpExecArray | null;
    while ((am = addrRe.exec(textExcerpt)) !== null) {
      const addr = formatXmlAddress(am[1] ?? "");
      if (addr) return addr;
    }
    const fallbackXmlAddr = formatXmlAddress(textExcerpt);
    if (fallbackXmlAddr) return fallbackXmlAddr;
  }

  const lines = textExcerpt
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const addrLabels = [
    "property address:",
    "property address",
    "subject address:",
    "subject property:",
    "location of property:",
    "determination address:",
    "re: property:",
    "property:",
    "premises:",
    "located at:",
  ];
  for (let i = 0; i < lines.length; i++) {
    const low = lines[i]!.toLowerCase();
    for (const lbl of addrLabels) {
      const pos = low.indexOf(lbl);
      if (pos !== -1) {
        const after = lines[i]!
          .slice(pos + lbl.length)
          .replace(/^[_|:\-\s]+|[_|:\-\s]+$/g, "");
        if (/^\d{1,6}\s+[A-Za-z]/.test(after)) {
          return after;
        }
        if (i + 1 < lines.length && /^\d{1,6}\s+[A-Za-z]/.test(lines[i + 1]!)) {
          return lines[i + 1]!.trim();
        }
      }
    }
  }
  return null;
}

/**
 * Extracts loan number from MISMO `<LoanIdentifier>` or PDF text (`TRN-BS01-0001`, `CASDW...`, `Lender Loan No.`).
 */
export function extractLoanNumber(fileName?: string | null, textExcerpt?: string | null): string | null {
  const text = `${fileName ?? ""}\n${textExcerpt ?? ""}`;
  if (text.includes("<")) {
    const xmlId = extractXmlTagInScope(text, "LoanIdentifier");
    if (xmlId && /\d/.test(xmlId)) {
      return xmlId.toUpperCase().replace(/^TRN-(BS|FD|DS)O/, "TRN-$10");
    }
  }
  const trnMatch = /\b(TRN-(?:BS|FD|DS)[0O]\d-[A-Z0-9]+)\b/i.exec(text);
  if (trnMatch?.[1]) {
    return trnMatch[1].toUpperCase().replace(/^TRN-(BS|FD|DS)O/, "TRN-$10");
  }
  const casdwMatch = /\b(CASDW?\d{6,12}|LS-\d{4}-[A-Z0-9-]+|DEMO-\d{4}-\d{3})\b/i.exec(text);
  if (casdwMatch?.[1]) {
    return casdwMatch[1].toUpperCase();
  }
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  for (let i = 0; i < lines.length - 1; i++) {
    if (lines[i]!.toLowerCase().includes("lender loan no./universal loan identifier")) {
      const next = lines[i + 1]!;
      if (/^\d{5,12}$/.test(next)) return next;
    }
  }
  const labeledMatch = /(?:lender\s*loan|loan\s*(?:number|no\.?|#))\s*[:#]\s*([A-Z0-9-]{5,24})/i.exec(
    text,
  );
  if (labeledMatch?.[1] && /\d/.test(labeledMatch[1])) {
    return labeledMatch[1].toUpperCase().replace(/^TRN-(BS|FD|DS)O/, "TRN-$10");
  }
  return null;
}

/**
 * Deterministic borrower/entity name extraction from borrower hint, MISMO XML tags / text headers,
 * or structured mortgage filenames (e.g. `Martinez_Elena_1003_URLA.xml`, `Apex_Holdings_Operating_Agreement_PTD_Stip.pdf`).
 */
export function extractBorrowerName(
  fileName: string,
  textExcerpt?: string,
  borrowerHint?: string,
): string {
  if (borrowerHint && borrowerHint.trim().length > 0) {
    return borrowerHint.trim();
  }

  const fromDoc = extractDocumentBorrowerOrEntity(textExcerpt);
  if (fromDoc) {
    return fromDoc;
  }

  const baseName = (fileName || "")
    .split(/[\\/]/)
    .pop()!
    .replace(/\.[a-zA-Z0-9]{1,6}$/, "")
    .trim();

  if (!baseName) return "Unassigned Borrower";

  const commaMatch = /^([A-Za-z'-]+)\s*,\s*([A-Za-z'-]+)(?:\b|[_-\s])/.exec(baseName);
  if (commaMatch) {
    return `${titleCaseToken(commaMatch[2])} ${titleCaseToken(commaMatch[1])}`;
  }

  const rawTokens = baseName.split(/[_-\s]+/).filter(Boolean);
  const nameTokens: string[] = [];

  for (const tok of rawTokens) {
    const lower = tok.toLowerCase();
    if (/^\d/.test(lower) || DOC_STOP_TOKENS.has(lower)) {
      break;
    }
    if (/^[a-zA-Z'.&]+$/.test(tok)) {
      nameTokens.push(tok);
    } else {
      break;
    }
    if (nameTokens.length >= 4) break;
  }

  if (nameTokens.length === 0) {
    return "Unassigned Borrower";
  }

  const hasEntityWord = nameTokens.some((t) => ENTITY_WORDS.has(t.toLowerCase()));
  if (hasEntityWord) {
    return nameTokens.map(titleCaseToken).join(" ");
  }

  if (nameTokens.length === 2) {
    const [first, second] = nameTokens;
    const firstLower = first.toLowerCase();
    const secondLower = second.toLowerCase();
    // Standard mortgage packaging pattern: LastName_FirstName_DocType (e.g., Martinez_Elena_1003_URLA.xml)
    if (COMMON_GIVEN_NAMES.has(secondLower) && !COMMON_GIVEN_NAMES.has(firstLower)) {
      return `${titleCaseToken(second)} ${titleCaseToken(first)}`;
    }
    return `${titleCaseToken(first)} ${titleCaseToken(second)}`;
  }

  return nameTokens.map(titleCaseToken).join(" ");
}

export const ExtractBorrowerName = extractBorrowerName;

function detectStatementPeriod(combined: string, rawText?: string): string | null {
  if (/\b24[_-\s]?(?:mo|month|months)\b/.test(combined)) return "24 months";
  if (/\b12[_-\s]?(?:mo|month|months)\b/.test(combined)) return "12 months";
  if (/\b6[_-\s]?(?:mo|month|months)\b/.test(combined)) return "6 months";
  if (/\b3[_-\s]?(?:mo|month|months)\b/.test(combined)) return "3 months";
  if (/\b2[_-\s]?(?:mo|month|months)\b/.test(combined)) return "2 months";
  if (rawText) {
    const extracted = extractStatementPeriod(rawText);
    if (extracted.status === "source_extracted" && extracted.start && extracted.end) {
      return `${extracted.start} to ${extracted.end}`;
    }
  }
  return null;
}

function buildPreviewWithLaya(
  base: {
    detectedBorrowerName: string;
    docCategory: DocCategory;
    docTypeLabel: string;
    fourLegsPillar: FourLegsPillar;
    routedSeat: RoutedSeat;
    routingReason: string;
    layaFamily: LayaDocFamily;
    statementPeriod: string | null;
    segmentationGuardTriggered: boolean;
  },
  rawContext?: string,
): SmartDropPreviewResult {
  const structuralFeatures = extractLayaStructuralFeatures(rawContext ?? "");
  const [firstLine = "", ...restLines] = (rawContext ?? "").split("\n");
  const contentText = restLines.join("\n");
  const emailTriage = extractEmailTriage(firstLine, contentText);
  const pageIntegrity = checkPageIntegrity(firstLine, contentText, undefined, base.layaFamily);
  const documentCompleteness = extractDocumentCompletenessDiagnostics(
    firstLine,
    contentText,
    pageIntegrity,
  );
  const extractedHighlights: LayaExtractedHighlights = {
    laya_family: base.layaFamily,
    statement_period: base.statementPeriod,
    segmentation_guard_triggered: base.segmentationGuardTriggered,
    model_checkpoint: LAYA_ARCHIVE_C6_CHECKPOINT,
    structural_features: structuralFeatures,
    page_integrity: {
      status: pageIntegrity.status,
      detected_pages: pageIntegrity.detectedPages,
      expected_pages: pageIntegrity.expectedPages,
      missing_pages: pageIntegrity.missingPages,
      advisory: pageIntegrity.advisory,
    },
    document_completeness: documentCompleteness,
    intake_warnings: documentCompleteness.warnings,
    ...(emailTriage ? { email_triage: emailTriage } : {}),
    authority: "advisory_unreviewed",
    originals_retained: true,
    lending_decision: "not_made",
  };
  return {
    ...base,
    extractedHighlights,
  };
}

/**
 * Instant client-side deterministic file classification & seat routing preview.
 * Mirrors the backend rules across the Four Legs (Application, Income, Credit, Assets, Equity, Conditions)
 * and Laya's 16-class document family (`extracted_highlights.laya_family`, `statement_period`, `mixed_page` guard).
 */
export function previewFileClassification(
  fileName: string,
  textExcerpt?: string,
  borrowerHint?: string,
): SmartDropPreviewResult {
  const detectedBorrowerName = extractBorrowerName(fileName, textExcerpt, borrowerHint);
  const lowerName = (fileName || "").toLowerCase();
  const lowerText = (textExcerpt || "").toLowerCase();
  const rawContext = `${fileName || ""}\n${textExcerpt || ""}`;
  const combined = `${lowerName.replace(/\.[a-z0-9]+$/, " ")} ${lowerText}`
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
  const statementPeriod = detectStatementPeriod(combined, rawContext);

  // 0. Laya `mixed_page` segmentation guard (mixed-document scan/packet held for segmentation review)
  if (
    /\b(mixed[_-\s]?page|mixed[_-\s]?packet|combined[_-\s]?scan|multi[_-\s]?doc[_-\s]?bundle|multi[_-\s]?panel|multiple\s+document\s+types)\b/.test(
      combined,
    )
  ) {
    return buildPreviewWithLaya(
      {
        detectedBorrowerName,
        docCategory: "other",
        docTypeLabel: "Mixed-Page Packet (Segmentation Hold)",
        fourLegsPillar: "conditions",
        routedSeat: "Processor",
        routingReason:
          "Mixed-page packet flagged by Laya segmentation guard (`mixed_page`); originals retained and held for human page-split review.",
        layaFamily: "mixed_page",
        statementPeriod,
        segmentationGuardTriggered: true,
      },
      rawContext,
    );
  }

  // 1. High-specificity Disclosures (Settlement Statement / Closing Disclosure / Initial Disclosures / Loan Estimate)
  // Checked before "statement" or "credit" so "Estimated Settlement Statement" with "Credit Report Fee" isn't hijacked.
  const isOfficialLoanEstimate =
    /\bloan\s+estimate\b/.test(combined) && !/get\s+an\s+official\s+loan\s+estimate/.test(combined);
  if (
    /\b(estimated\s+settlement\s+statement|settlement\s+statement|closing\s+disclosure|initial\s+disclosures|intent\s+to\s+proceed|econsent|credit\s+score\s+disclosure|fee\s+worksheet)\b/.test(
      combined,
    ) ||
    isOfficialLoanEstimate
  ) {
    return buildPreviewWithLaya(
      {
        detectedBorrowerName,
        docCategory: "disclosures",
        docTypeLabel: "Loan Disclosures & Acknowledgments",
        fourLegsPillar: "application",
        routedSeat: "Processor",
        routingReason:
          "Disclosure acknowledgments, settlement statements, and compliance packets route to Packaging / Processor.",
        layaFamily: "condition_stip_loe",
        statementPeriod: null,
        segmentationGuardTriggered: false,
      },
      rawContext,
    );
  }

  // 2. Condition Stips, Conditional Approval & Entity Clearing Documents -> AM
  const hasConditionalApproval =
    /\b(conditional\s+loan\s+approval|conditional\s+approval|conditions\s+of\s+approval|conditions\s+for\s+approval|underwriting\s+conditions|prior\s+to\s+doc|prior\s+to\s+funding)\b/.test(
      combined,
    );
  const hasConditionStipToken =
    /\b(stip|stips|ptd|ptf|ptc|clearing)\b/.test(combined) ||
    (/\b(condition|conditions)\b/.test(combined) &&
      !/\b(property\s+condition|general\s+conditions|market\s+conditions)\b/.test(combined));
  if (
    hasConditionalApproval ||
    hasConditionStipToken ||
    /(?:operating[_-\s]?agreement|articles[_-\s]?of[_-\s]?organization|good[_-\s]?standing|ein[_-\s]?letter|letter[_-\s]?of[_-\s]?explanation|\bloe\b)/.test(
      combined,
    )
  ) {
    const isEntityStip =
      !hasConditionalApproval &&
      /(?:operating[_-\s]?agreement|articles[_-\s]?of[_-\s]?organization|good[_-\s]?standing|\bein\b)/.test(
        combined,
      );
    const isLoe = /\b(loe|letter[_-\s]?of[_-\s]?explanation)\b/.test(combined);
    const docTypeLabel = isEntityStip
      ? "Operating Agreement / Entity Stip"
      : isLoe
        ? "Letter of Explanation (Condition Stip)"
        : "Underwriting Condition Stip";
    return buildPreviewWithLaya(
      {
        detectedBorrowerName,
        docCategory: "conditions",
        docTypeLabel,
        fourLegsPillar: "conditions",
        routedSeat: "AM",
        routingReason:
          "PTD/PTF condition stipulations and entity clearing documents route directly to your Account Manager (AM).",
        layaFamily: isEntityStip ? "entity_formation" : "condition_stip_loe",
        statementPeriod,
        segmentationGuardTriggered: false,
      },
      rawContext,
    );
  }

  // 3. Purchase Agreement / Seller Counter Offer / Addendum (guarded before "credit" or "loan application")
  if (
    /\b(purchase\s+agreement|purchase\s+contract|residential\s+purchase\s+agreement|real\s+estate\s+purchase\s+contract|seller\s+counter\s+offer|counter\s+offer|sales\s+contract|earnest\s+money)\b/.test(
      combined,
    )
  ) {
    return buildPreviewWithLaya(
      {
        detectedBorrowerName,
        docCategory: "property",
        docTypeLabel: "Executed Purchase Contract",
        fourLegsPillar: "equity",
        routedSeat: "CA",
        routingReason:
          "Appraisals, BPOs, and purchase contracts route to your Credit Analyst (CA) for Four-Legs Equity & collateral review.",
        layaFamily: "purchase_contract",
        statementPeriod: null,
        segmentationGuardTriggered: false,
      },
      rawContext,
    );
  }

  // 4. AirDNA / Short-Term Rental Projections -> Income / Lease & Rent Roll
  // Guarded against insurance quotes that state "Short-Term Rental: No" or "Homeowner's Insurance Quote"
  if (
    /\b(airdna|rentalizer)\b/.test(combined) ||
    (/\b(short\s+term\s+rental|vacation\s+rental)\b/.test(combined) &&
      !/\b(short\s+term\s+rental\s*:\s*no|insurance\s+quote|homeowner'?s\s+insurance|dwelling\s+fire|policy\s+coverages)\b/.test(
        combined,
      ))
  ) {
    return buildPreviewWithLaya(
      {
        detectedBorrowerName,
        docCategory: "income",
        docTypeLabel: "Executed Lease / Rent Roll",
        fourLegsPillar: "income",
        routedSeat: "CA",
        routingReason:
          "Income, tax, P&L, and lease cash-flow documents route to your Credit Analyst (CA) for Four-Legs Income qualification.",
        layaFamily: "lease_rent_roll",
        statementPeriod,
        segmentationGuardTriggered: false,
      },
      rawContext,
    );
  }

  // 5. 1003 URLA / MISMO 3.4 XML / Pre-Qualification / Advisory PQ / SEQ -> AE
  if (
    lowerName.endsWith(".xml") ||
    lowerName.endsWith(".fnm") ||
    /\b(1003|urla|mismo|loan[_-\s]?application|pre[_-\s]?qualification|pre[_-\s]?qual|prequal|advisory[_-\s]?pq|pq[_-\s]?form|self[_-\s]?employed[_-\s]?questionnaire)\b/.test(
      combined,
    ) ||
    /<(?:[a-z0-9_]+:)?(?:message|deal_sets|loan_application)\b/.test(lowerText)
  ) {
    const isPrequal = /\b(pre[_-\s]?qualification|pre[_-\s]?qual|prequal|advisory[_-\s]?pq|pq[_-\s]?form)\b/.test(
      combined,
    );
    const isSeq = /\b(self[_-\s]?employed[_-\s]?questionnaire)\b/.test(combined);
    return buildPreviewWithLaya(
      {
        detectedBorrowerName,
        docCategory: "application",
        docTypeLabel: isSeq
          ? "Self-Employed Questionnaire (SEQ)"
          : isPrequal
            ? "Pre-Qualification & Submission Summary"
            : "1003 URLA Loan Application",
        fourLegsPillar: isSeq ? "income" : "application",
        routedSeat: isSeq ? "CA" : "AE",
        routingReason:
          "1003 URLA applications and MISMO 3.4 intake files route to your Account Executive (AE) for scenario & Pre-Qual structuring.",
        layaFamily: "urla_1003",
        statementPeriod: null,
        segmentationGuardTriggered: false,
      },
      rawContext,
    );
  }

  // 6. Bank Statements / VOD / Reserves / Asset Statements -> CA
  // Checked before generic "credit" so bank statements with "Deposits and Credits" or "Line of Credit" aren't hijacked.
  const hasExplicitCreditBureau =
    /\b(trimerge|tri[_-\s]?merge|cbr|experian|equifax|transunion|credit[_-\s]?report|vome|mortgage[_-\s]?history)\b/.test(
      combined,
    ) && !/\bcredit\s+report\s+fee\b/.test(combined);
  const hasBankStatementSignal =
    /\b(bank|statement|statements|stmt|stmts|estmt|e\s+stmt|vod|reserves|checking|savings|brokerage|asset|assets|deposits\s+and\s+credits|beginning\s+balance|ending\s+balance|ubs\s+portfolio|portfolio\s+summary|adv\s+plus\s+banking)\b/.test(
      combined,
    ) &&
    !/\b(income\s+statement|income\s+expense\s+statement|profit\s+and\s+loss\s+statement|earnings\s+statement|wage\s+and\s+tax\s+statement|statement\s+of\s+information|hoa\s+dues\s+statement|mtg\s+stmt|w2stmt|taxstatement|tax\s+statement)\b/.test(
      combined,
    );

  if (hasBankStatementSignal && !hasExplicitCreditBureau) {
    const is12Mo = statementPeriod === "12 months";
    const is24Mo = statementPeriod === "24 months";
    const isVod = /\bvod\b|verification[_-\s]?of[_-\s]?deposit/.test(combined);
    const docTypeLabel = is12Mo
      ? "12-Month Bank Statements"
      : is24Mo
        ? "24-Month Bank Statements"
        : isVod
          ? "Verification of Deposit (VOD)"
          : "Bank & Asset Statements";
    return buildPreviewWithLaya(
      {
        detectedBorrowerName,
        docCategory: "assets",
        docTypeLabel,
        fourLegsPillar: "assets",
        routedSeat: "CA",
        routingReason:
          "Bank statements, deposit history, and reserve verifications route to your Credit Analyst (CA) for Four-Legs Cash/Assets & cash-flow review.",
        layaFamily: isVod ? "asset_verification" : "bank_statement",
        statementPeriod,
        segmentationGuardTriggered: false,
      },
      rawContext,
    );
  }

  // 7. Credit Report / Housing History -> CA
  if (
    hasExplicitCreditBureau ||
    (/\b(credit|fico|bankruptcy)\b/.test(combined) &&
      !/\b(deposits\s+and\s+credits|seller\s+credit|lender\s+credit|tax\s+credit|credit\s+report\s+fee|credit\s+union)\b/.test(
        combined,
      ))
  ) {
    return buildPreviewWithLaya(
      {
        detectedBorrowerName,
        docCategory: "credit",
        docTypeLabel: "Tri-Merge Credit Report",
        fourLegsPillar: "credit",
        routedSeat: "CA",
        routingReason:
          "Tri-merge credit reports and housing history route to your Credit Analyst (CA) for Four-Legs Credit evaluation.",
        layaFamily: "credit_report",
        statementPeriod: null,
        segmentationGuardTriggered: false,
      },
      rawContext,
    );
  }

  // 8. Property Tax Bill guard -> Property (before Tax Returns)
  if (
    /\b(property\s+tax\s+bill|secured\s+property\s+tax|annual\s+secured\s+bills|county\s+tax\s+collector)\b/.test(
      combined,
    )
  ) {
    return buildPreviewWithLaya(
      {
        detectedBorrowerName,
        docCategory: "property",
        docTypeLabel: "Property Appraisal Report",
        fourLegsPillar: "equity",
        routedSeat: "CA",
        routingReason:
          "Property tax bills, appraisals, and collateral documents route to your Credit Analyst (CA) for Four-Legs Equity review.",
        layaFamily: "appraisal_valuation",
        statementPeriod: null,
        segmentationGuardTriggered: false,
      },
      rawContext,
    );
  }

  // 9. Tax Returns / Lease Agreements / Rent Roll / P&L / W-2 / Paystub / Income -> CA
  if (
    /\b(tax|1040|1120|1120s|1065|k1|w2|1099|paystub|paystubs|pay\s+stub|earnings\s+statement|gross\s+pay|net\s+pay|pnl|profit[_-\s]?(?:and|&)[_-\s]?loss|cpa|lease|leases|rent[_-\s]?roll|dscr|income|voe|verification\s+of\s+employment)\b/.test(
      combined,
    ) &&
    !/\b(taxes\s+and\s+insurance|transfer\s+tax)\b/.test(combined)
  ) {
    const isLease = /\b(lease|leases|rent[_-\s]?roll|dscr)\b/.test(combined);
    const isPnl = /\b(pnl|profit|cpa)\b/.test(combined);
    const isBizTax = /\b(1120|1120s|1065|k1)\b/.test(combined);
    const isW2OrPaystub = /\b(w2|1099|paystub|paystubs|pay\s+stub|earnings\s+statement|gross\s+pay|net\s+pay|voe|verification\s+of\s+employment)\b/.test(
      combined,
    );
    const isTax = (!isW2OrPaystub && /\b(tax|1040)\b/.test(combined)) || isBizTax;
    const docTypeLabel = isLease
      ? "Executed Lease / Rent Roll"
      : isPnl
        ? "CPA Profit & Loss (P&L) Statement"
        : isTax
          ? "Tax Returns & Schedules"
          : "Income & Cash Flow Documentation";
    const layaFamily: LayaDocFamily = isLease
      ? "lease_rent_roll"
      : isPnl
        ? "profit_and_loss"
        : isBizTax
          ? "business_tax_return"
          : isTax
            ? "tax_return_1040"
            : "w2_1099_paystub";
    return buildPreviewWithLaya(
      {
        detectedBorrowerName,
        docCategory: "income",
        docTypeLabel,
        fourLegsPillar: "income",
        routedSeat: "CA",
        routingReason:
          "Income, tax, P&L, and lease cash-flow documents route to your Credit Analyst (CA) for Four-Legs Income qualification.",
        layaFamily,
        statementPeriod,
        segmentationGuardTriggered: false,
      },
      rawContext,
    );
  }

  // 10. Appraisal / BPO / Purchase Contract / Property Collateral -> CA
  if (
    /\b(appraisal|1004|1007|1025|1073|bpo|cda|purchase[_-\s]?(?:contract|agreement)|psa|property|collateral|inspection|arv|rehab)\b/.test(
      combined,
    )
  ) {
    const isContract = /\b(purchase|contract|psa)\b/.test(combined);
    const isBpo = /\b(bpo|cda)\b/.test(combined);
    const docTypeLabel = isContract
      ? "Executed Purchase Contract"
      : isBpo
        ? "BPO / Collateral Desktop Review"
        : "Property Appraisal Report";
    return buildPreviewWithLaya(
      {
        detectedBorrowerName,
        docCategory: "property",
        docTypeLabel,
        fourLegsPillar: "equity",
        routedSeat: "CA",
        routingReason:
          "Appraisals, BPOs, and purchase contracts route to your Credit Analyst (CA) for Four-Legs Equity & collateral review.",
        layaFamily: isContract ? "purchase_contract" : "appraisal_valuation",
        statementPeriod: null,
        segmentationGuardTriggered: false,
      },
      rawContext,
    );
  }

  // 11. Title / Hazard Insurance / Preliminary Commitment / Escrow -> Processor
  if (
    /\b(title|prelim|preliminary|commitment|cpl|hazard|hoi|insurance|flood|binder|escrow|wire|survey)\b/.test(
      combined,
    )
  ) {
    const isInsurance = /\b(hazard|hoi|insurance|flood|binder)\b/.test(combined);
    const docTypeLabel = isInsurance
      ? "Hazard / Property Insurance Binder"
      : "Preliminary Title Commitment";
    return buildPreviewWithLaya(
      {
        detectedBorrowerName,
        docCategory: "title_insurance",
        docTypeLabel,
        fourLegsPillar: "equity",
        routedSeat: "Processor",
        routingReason:
          "Title commitments, escrow instructions, and insurance binders route to Packaging / Processor & Account Management.",
        layaFamily: isInsurance ? "hazard_insurance" : "title_commitment",
        statementPeriod: null,
        segmentationGuardTriggered: false,
      },
      rawContext,
    );
  }

  // 12. Disclosures -> Processor
  if (
    /\b(disclosure|disclosures|intent[_-\s]?to[_-\s]?proceed|econsent|closing[_-\s]?disclosure|loan[_-\s]?estimate)\b/.test(
      combined,
    )
  ) {
    return buildPreviewWithLaya(
      {
        detectedBorrowerName,
        docCategory: "disclosures",
        docTypeLabel: "Loan Disclosures & Acknowledgments",
        fourLegsPillar: "application",
        routedSeat: "Processor",
        routingReason:
          "Disclosure acknowledgments and compliance packets route to Packaging / Processor.",
        layaFamily: "condition_stip_loe",
        statementPeriod: null,
        segmentationGuardTriggered: false,
      },
      rawContext,
    );
  }

  // 13. Fallback
  return buildPreviewWithLaya(
    {
      detectedBorrowerName,
      docCategory: "other",
      docTypeLabel: "Supporting Loan Document",
      fourLegsPillar: "conditions",
      routedSeat: "AM",
      routingReason:
        "Supporting loan files route to your Account Manager (AM) for file indexing and condition matching.",
      layaFamily: "condition_stip_loe",
      statementPeriod,
      segmentationGuardTriggered: false,
    },
    rawContext,
  );
}

/**
 * Groups `SmartDropItem[]` by `detected_borrower_name`, preserving newest first,
 * with summary metadata (`borrowerName`, `items`, `routedSeats`, `pillars`, `matchedLoanId`, `loName`, `companyName`).
 */
export function groupItemsByBorrower(items: SmartDropItem[]): BorrowerGroup[] {
  const sorted = [...items].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const map = new Map<string, BorrowerGroup>();

  for (const item of sorted) {
    const borrowerName = (item.detected_borrower_name || "").trim() || "Unassigned Borrower";
    let group = map.get(borrowerName);
    if (!group) {
      group = {
        borrowerName,
        items: [],
        routedSeats: [],
        pillars: [],
        matchedLoanId: null,
        loName: null,
        companyName: null,
      };
      map.set(borrowerName, group);
    }
    group.items.push(item);
    if (!group.routedSeats.includes(item.routed_seat)) {
      group.routedSeats.push(item.routed_seat);
    }
    if (!group.pillars.includes(item.four_legs_pillar)) {
      group.pillars.push(item.four_legs_pillar);
    }
    if (!group.matchedLoanId && item.loan_id) {
      group.matchedLoanId = item.loan_id;
    }
    if (!group.loName && item.lo_name) {
      group.loName = item.lo_name;
    }
    if (!group.companyName && item.company_name) {
      group.companyName = item.company_name;
    }
  }

  return Array.from(map.values());
}

const MAX_UPLOAD_SLICE_BYTES = 256 * 1024;
const MAX_TEXT_EXCERPT_BYTES = 32 * 1024;

function inferMimeType(fileName: string): string {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".xml")) return "application/xml";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".csv")) return "text/csv";
  if (lower.endsWith(".txt")) return "text/plain";
  if (lower.endsWith(".xlsx"))
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (lower.endsWith(".docx"))
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  return "application/octet-stream";
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const sub = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...sub);
  }
  return typeof btoa === "function" ? btoa(binary) : "";
}

async function readBlobBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === "function") {
    const buf = await blob.arrayBuffer();
    return new Uint8Array(buf);
  }
  return new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(reader.result));
      } else {
        resolve(new Uint8Array(0));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Reads up to 256 KB of a File for base64 & text excerpt (XML/txt/PDF header preview)
 * so multi-file drops upload ultra-fast and reliably.
 */
export async function fileToUploadPayload(file: File): Promise<SmartDropFilePayload> {
  const slice = file.slice(0, MAX_UPLOAD_SLICE_BYTES);
  const bytes = await readBlobBytes(slice);
  const content_base64 = bytesToBase64(bytes);

  const excerptBytes = bytes.subarray(0, MAX_TEXT_EXCERPT_BYTES);
  const rawText = new TextDecoder("utf-8", { fatal: false }).decode(excerptBytes);
  // Strip NUL and control characters while keeping readable ASCII/UTF-8 text & XML tags
  let cleaned = "";
  for (let i = 0; i < rawText.length; i++) {
    const code = rawText.charCodeAt(i);
    if ((code < 32 && code !== 9 && code !== 10 && code !== 13) || code === 127) {
      cleaned += " ";
    } else {
      cleaned += rawText[i];
    }
  }
  const text_excerpt = cleaned.replace(/\s+/g, " ").trim().slice(0, 4096);

  return {
    file_name: file.name,
    mime_type: file.type || inferMimeType(file.name),
    byte_size: file.size,
    content_base64,
    text_excerpt,
  };
}

/** Build a sample multi-borrower LO drop bundle (no lending rates/thresholds) for 1-click demo staging. */
export function buildSampleSmartDropFiles(): File[] {
  const mismoXml = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<MESSAGE xmlns="http://www.mismo.org/residential/2009/schemas">',
    "  <DEAL_SETS>",
    "    <DEAL_SET>",
    "      <DEALS>",
    "        <DEAL>",
    "          <PARTIES>",
    "            <PARTY>",
    "              <INDIVIDUAL>",
    "                <NAME>",
    "                  <FirstName>Elena</FirstName>",
    "                  <LastName>Martinez</LastName>",
    "                  <FullName>Elena Martinez</FullName>",
    "                </NAME>",
    "              </INDIVIDUAL>",
    "            </PARTY>",
    "          </PARTIES>",
    "          <COLLATERALS>",
    "            <COLLATERAL>",
    "              <SUBJECT_PROPERTY>",
    "                <ADDRESS>",
    "                  <AddressLineText>742 Evergreen Terrace, San Diego, CA 92101</AddressLineText>",
    "                </ADDRESS>",
    "              </SUBJECT_PROPERTY>",
    "            </COLLATERAL>",
    "          </COLLATERALS>",
    "        </DEAL>",
    "      </DEALS>",
    "    </DEAL_SET>",
    "  </DEAL_SETS>",
    "</MESSAGE>",
  ].join("\n");

  const bankPdf =
    "%PDF-1.4\n% Borrower: Elena Martinez\n% Institution: Chase Bank #4821\n% Document: 12-Month Business Bank Statements (2025-01 to 2025-12)\n% Property: 742 Evergreen Terrace, San Diego, CA 92101\n% Page 1 of 2\n% Page 2 of 2\n%%EOF";
  const creditPdf =
    "%PDF-1.4\n% Borrower: Elena Martinez\n% Document: Tri-Merge Credit Report\n% Page 1 of 3\n% Page 2 of 3\n% Page 3 of 3\n%%EOF";
  const stipPdf =
    "%PDF-1.4\n% Entity Name: Apex Holdings\n% Document: Operating Agreement - PTD Condition Stip\n% Page 1 of 2\n% Page 2 of 2\n%%EOF";

  return [
    new File([mismoXml], "Martinez_Elena_1003_URLA.xml", { type: "application/xml" }),
    new File([bankPdf], "Martinez_Elena_12Mo_Bank_Statements.pdf", { type: "application/pdf" }),
    new File([creditPdf], "Martinez_Elena_TriMerge_Credit.pdf", { type: "application/pdf" }),
    new File([stipPdf], "Apex_Holdings_Operating_Agreement_PTD_Stip.pdf", {
      type: "application/pdf",
    }),
  ];
}

// ---- Pre-Underwriting Diagnostics, Native LS2 e-Signature & 4-Legs Readiness ----

export interface PageIntegrityReport {
  status: "verified_complete" | "missing_pages_detected" | "screenshot_warning" | "unverified";
  detectedPages: number[];
  expectedPages: number | null;
  missingPages: number[];
  advisory: string | null;
}

/**
 * Deterministic client-side page-count & native statement integrity verifier.
 * Detects `Page X of Y` markers, computes missing page numbers, and flags mobile screenshots.
 */
export function checkPageIntegrity(
  fileName: string,
  textExcerpt?: string,
  mimeType?: string,
  layaFamily?: string,
): PageIntegrityReport {
  const lowerName = (fileName || "").toLowerCase();
  const lowerText = (textExcerpt || "").toLowerCase();
  const lowerMime = (mimeType || "").toLowerCase();
  const combined = `${lowerName} ${lowerText}`.replace(/[_-]+/g, " ");

  const isScreenshotNameOrText = /\b(screenshot|screen\s*shot|screen\s*capture)\b/.test(
    combined,
  );
  const isImageMime =
    lowerMime.startsWith("image/") ||
    lowerName.endsWith(".png") ||
    lowerName.endsWith(".jpg") ||
    lowerName.endsWith(".jpeg");
  const isBankOrAssetDoc =
    layaFamily === "bank_statement" ||
    layaFamily === "asset_verification" ||
    /\b(bank|statement|statements|stmt|stmts|vod|checking|savings)\b/.test(combined);

  if (isScreenshotNameOrText || (isImageMime && isBankOrAssetDoc)) {
    return {
      status: "screenshot_warning",
      detectedPages: [],
      expectedPages: null,
      missingPages: [],
      advisory:
        "Image or mobile screenshot detected. Underwriting requires the native PDF statement downloaded directly from the financial institution with all numbered pages.",
    };
  }

  const pageRegex = /\b(?:page|pg\.?)\s*(\d{1,3})\s*(?:of|\/)\s*(\d{1,3})\b/gi;
  const detectedSet = new Set<number>();
  let expectedPages: number | null = null;
  let match: RegExpExecArray | null = pageRegex.exec(textExcerpt || "");
  while (match !== null) {
    const pageNum = Number.parseInt(match[1], 10);
    const totalNum = Number.parseInt(match[2], 10);
    if (Number.isFinite(pageNum) && pageNum > 0 && pageNum <= 500) {
      detectedSet.add(pageNum);
    }
    if (Number.isFinite(totalNum) && totalNum > 0 && totalNum <= 500) {
      expectedPages = expectedPages === null ? totalNum : Math.max(expectedPages, totalNum);
    }
    match = pageRegex.exec(textExcerpt || "");
  }

  const detectedPages = Array.from(detectedSet).sort((a, b) => a - b);
  const explicitMissingNote = /\bmissing[_-\s]+pages?\b/.test(combined);

  if (expectedPages !== null && expectedPages > 0) {
    const missingPages: number[] = [];
    for (let p = 1; p <= expectedPages; p++) {
      if (!detectedSet.has(p)) {
        missingPages.push(p);
      }
    }

    if (missingPages.length > 0 || explicitMissingNote) {
      return {
        status: "missing_pages_detected",
        detectedPages,
        expectedPages,
        missingPages,
        advisory:
          missingPages.length > 0
            ? `Missing page(s) ${missingPages.join(", ")} of ${expectedPages} detected. Please upload the complete multi-page statement PDF including all trailing pages.`
            : `Missing page indicator detected for ${expectedPages}-page document. Please upload all pages.`,
      };
    }

    return {
      status: "verified_complete",
      detectedPages,
      expectedPages,
      missingPages: [],
      advisory: `Verified complete (${detectedPages.length} of ${expectedPages} pages present).`,
    };
  }

  if (explicitMissingNote) {
    return {
      status: "missing_pages_detected",
      detectedPages,
      expectedPages: null,
      missingPages: [],
      advisory:
        "Missing page note detected in document. Please upload the complete unredacted PDF with all pages.",
    };
  }

  return {
    status: "unverified",
    detectedPages: [],
    expectedPages: null,
    missingPages: [],
    advisory: null,
  };
}

/**
 * Deterministic document completeness, execution & defect diagnostics.
 * Detects removed/placeholder slips, redacted signatures/identities, truncated multi-page statements,
 * image-only 0-text PDFs requiring OCR, and garbled subset-font encoding streams.
 */
export function extractDocumentCompletenessDiagnostics(
  fileName: string,
  contentText?: string | null,
  pageIntegrity?: PageIntegrityReport | { status?: string; missingPages?: number[]; missing_pages?: number[]; advisory?: string | null },
  textExtractionAttempted?: boolean,
): DocumentCompletenessDiagnostics {
  const fileLower = (fileName || "").toLowerCase();
  const trimmed = (contentText ?? "").trim();
  const contentLower = trimmed.toLowerCase();
  const pi = pageIntegrity ?? checkPageIntegrity(fileName, contentText ?? undefined);

  const placeholder_or_removed_document =
    fileLower.includes("document-removed") ||
    fileLower.includes("placeholder") ||
    contentLower.includes("placeholder — original") ||
    contentLower.includes("placeholder - original") ||
    contentLower.includes("removed during de-identification") ||
    contentLower.includes("document removed");

  const signature_or_identity_redacted =
    contentLower.includes("[signature redacted]") ||
    contentLower.includes("[entity redacted]") ||
    contentLower.includes("[borrower redacted]") ||
    contentLower.includes("[name redacted]") ||
    contentLower.includes("signature redacted");

  const truncated_or_missing_pages = pi.status === "missing_pages_detected";

  const isPdf = fileLower.endsWith(".pdf");
  const image_only_scan_ocr_needed =
    isPdf &&
    trimmed.length === 0 &&
    (textExtractionAttempted === undefined ? contentText !== undefined : textExtractionAttempted);

  const isGarbledChar = (code: number): boolean =>
    (code > 0 && code < 32 && code !== 9 && code !== 10 && code !== 12 && code !== 13) ||
    code === 0xfffd ||
    code === 0x00ff;

  let ctrlAll = 0;
  let ctrlHead = 0;
  for (let i = 0; i < trimmed.length; i++) {
    if (isGarbledChar(trimmed.charCodeAt(i))) {
      ctrlAll++;
      if (i < 200) ctrlHead++;
    }
  }
  const garbled_font_encoding =
    trimmed.length > 0 &&
    ((ctrlAll >= 20 && ctrlAll * 5 >= trimmed.length) || ctrlHead >= 20);

  const defect_codes: string[] = [];
  const warnings: DocumentCompletenessWarning[] = [];

  if (placeholder_or_removed_document) {
    defect_codes.push("placeholder_or_removed_document");
    warnings.push({
      code: "placeholder_or_removed_document",
      severity: "warning",
      message:
        "Document is a placeholder or removed-document slip; request the original underlying document.",
    });
  }
  if (signature_or_identity_redacted) {
    defect_codes.push("signature_or_identity_redacted");
    warnings.push({
      code: "signature_or_identity_redacted",
      severity: "warning",
      message:
        "Signature or identity field is redacted ([SIGNATURE REDACTED] / [ENTITY REDACTED]); verify unredacted execution copy.",
    });
  }
  if (truncated_or_missing_pages) {
    defect_codes.push("truncated_or_missing_pages");
    const missingPages =
      ("missingPages" in pi && Array.isArray(pi.missingPages)
        ? pi.missingPages
        : "missing_pages" in pi && Array.isArray(pi.missing_pages)
          ? pi.missing_pages
          : []) as number[];
    warnings.push({
      code: "truncated_or_missing_pages",
      severity: "warning",
      missing_pages: missingPages,
      message: pi.advisory ?? "Document is missing expected pages.",
    });
  }
  if (image_only_scan_ocr_needed) {
    defect_codes.push("image_only_scan_ocr_needed");
    warnings.push({
      code: "image_only_scan_ocr_needed",
      severity: "info",
      message:
        "PDF contains scanned page images with no extractable text layer; route through OCR before automated field extraction.",
    });
  }
  if (garbled_font_encoding) {
    defect_codes.push("garbled_font_encoding");
    warnings.push({
      code: "garbled_font_encoding",
      severity: "warning",
      message:
        "PDF text stream contains unmapped subset font glyphs or control-character corruption; OCR fallback recommended.",
    });
  }

  return {
    status: defect_codes.length === 0 ? "complete" : "attention_needed",
    placeholder_or_removed_document,
    signature_or_identity_redacted,
    truncated_or_missing_pages,
    image_only_scan_ocr_needed,
    garbled_font_encoding,
    defect_codes,
    warnings,
  };
}

export interface StatementContinuitySummary {
  institution: string | null;
  accountLast4: string | null;
  accountType?:
    | "business_checking"
    | "personal_checking"
    | "savings"
    | "brokerage_retirement"
    | "payment_processor"
    | "deposit_account";
  coveredMonths: string[];
  missingMonths: string[];
  targetMonths: number;
  isComplete: boolean;
  continuityStatus?:
    | "complete_24_month"
    | "complete_12_month"
    | "contiguous_partial"
    | "gaps_detected";
}

function expandYearMonthRange(startYm: string, endYm: string): string[] {
  const [sy, sm] = startYm.split("-").map((v) => Number.parseInt(v, 10));
  const [ey, em] = endYm.split("-").map((v) => Number.parseInt(v, 10));
  if (!Number.isFinite(sy) || !Number.isFinite(sm) || !Number.isFinite(ey) || !Number.isFinite(em)) {
    return [];
  }
  const startTotal = sy * 12 + (sm - 1);
  const endTotal = ey * 12 + (em - 1);
  if (endTotal < startTotal || endTotal - startTotal > 36) {
    return [];
  }
  const months: string[] = [];
  for (let m = startTotal; m <= endTotal; m++) {
    const year = Math.floor(m / 12);
    const month = (m % 12) + 1;
    months.push(`${year}-${String(month).padStart(2, "0")}`);
  }
  return months;
}

/**
 * Analyzes uploaded or staged bank statement items to extract institution name, account last-4,
 * covered `YYYY-MM` statement months, and any missing month gaps across a 12-month (or 24-month) continuity window.
 */
export function analyzeStatementContinuity(
  items: Array<{
    file_name: string;
    text_excerpt?: string;
    extracted_highlights?: Record<string, unknown>;
  }>,
): StatementContinuitySummary {
  let institution: string | null = null;
  let accountLast4: string | null = null;
  let accountType: StatementContinuitySummary["accountType"] = "deposit_account";
  let targetMonths = 12;
  const coveredSet = new Set<string>();
  let has12MoShorthand = false;
  let has24MoShorthand = false;
  let detectedYear = "2025";

  const institutionPatterns: Array<[RegExp, string]> = [
    [/\b(?:jpmorgan\s+chase|chase\s+bank|chase)\b/i, "Chase"],
    [/\bwells[_-\s]*fargo\b/i, "Wells Fargo"],
    [/\b(?:bank[_-\s]+of[_-\s]+america|bankofamerica|bofa|your\s+adv\s+plus\s+banking|your\s+business\s+advantage)\b/i, "Bank of America"],
    [/\bmercury\b/i, "Mercury"],
    [/\brelay(?!\s+calls)\b/i, "Relay"],
    [/\bfirst[_-\s]+republic\b/i, "First Republic"],
    [/\b(?:citibank|citicards|citi)\b/i, "Citibank"],
    [/\bfidelity\b/i, "Fidelity"],
    [/\b(?:charles\s+schwab|schwab)\b/i, "Schwab"],
    [/\bvanguard\b/i, "Vanguard"],
    [/\bedward\s*jones\b/i, "Edward Jones"],
    [/\bempower(?:\s+retirement)?\b/i, "Empower"],
    [/\bu\.?s\.?[_-\s]+bank\b/i, "US Bank"],
    [/\bpnc(?:\s+bank)?\b/i, "PNC"],
    [/\bcapital[_-\s]+one\b/i, "Capital One"],
    [/\btd[_-\s]+bank\b/i, "TD Bank"],
    [/\bvenmo\b/i, "Venmo"],
    [/\bpaypal\b/i, "PayPal"],
    [/\b(?:thrift\s+savings\s+plan|tsp\.gov|\btsp\b)\b/i, "Thrift Savings Plan"],
    [/\bmorgan\s+stanley\b/i, "Morgan Stanley"],
    [/\be\*?trade\b/i, "E*TRADE"],
    [/\bmerrill\b/i, "Merrill"],
    [/\btruist\b/i, "Truist"],
    [/\bregions(?:\s+bank)?\b/i, "Regions"],
    [/\bfifth\s+third\b/i, "Fifth Third"],
    [/\bbmo\b/i, "BMO"],
    [/\bhuntington\b/i, "Huntington"],
    [/\bkeybank\b/i, "KeyBank"],
    [/\bnavy\s+federal\b/i, "Navy Federal"],
    [/\busaa\b/i, "USAA"],
    [/\bally(?:\s+bank)?\b/i, "Ally"],
    [/\bsofi\b/i, "SoFi"],
    [/\baxos\b/i, "Axos"],
    [/\bfirst\s+horizon\b/i, "First Horizon"],
    [/\bcitizens(?:\s+bank)?\b/i, "Citizens"],
    [/\bm&t\s+bank\b/i, "M&T Bank"],
    [/\bzions\b/i, "Zions"],
    [/\bcomerica\b/i, "Comerica"],
    [/\beast\s+west\s+bank\b/i, "East West Bank"],
    [/\bcathay\s+bank\b/i, "Cathay Bank"],
    [/\ballegacy\b/i, "Allegacy"],
    [/\bmetairie\s+bank\b/i, "Metairie Bank"],
    [/\bbank\s+of\s+the\s+sierra\b/i, "Bank of the Sierra"],
    [/\bnoble[_-\s]+bank\b/i, "Noble Bank"],
    [/\b(?:banner\s*bank|connected\s+checking)\b/i, "Banner Bank"],
    [/\brockland\s*trust\b/i, "Rockland Trust"],
    [/\blafcu\b/i, "Los Angeles Federal Credit Union"],
    [/\bfirst\s+american\s+trust\b/i, "First American Trust"],
    [/\bamerican\s*express\b/i, "American Express"],
    [/\bsynthetic\s+first\s+bank\b/i, "Synthetic First Bank"],
    [/\bsynthetic\s+credit\s+union\b/i, "Synthetic Credit Union"],
    [/\bselene\s*finance\b/i, "Selene Finance"],
    [/\b(?:select\s+portfolio\s+servicing|spservicing)\b/i, "Select Portfolio Servicing"],
    [/\bnewrez\b/i, "NewRez"],
    [/\bnational\s+bank\b/i, "National Bank"],
    [/\bubs\b/i, "UBS"],
    [/\blpl\s+financial\b/i, "LPL Financial"],
    [/\bvera\s*bank\b/i, "VeraBank"],
    [/\bcalifornia\s+bank\s+of\s+commerce\b/i, "California Bank of Commerce"],
    [/\bbanamex\b/i, "Banamex"],
    [/\bi[nm]bursa\b/i, "Inbursa"],
  ];

  for (const item of items) {
    const combinedRaw = `${item.file_name || ""}\n${item.text_excerpt || ""}`;
    const combinedNormalized = combinedRaw.replace(/[_]+/g, " ").replace(/\.[a-zA-Z0-9]+/g, " ");
    const lowerCombined = combinedNormalized.toLowerCase();
    const headerLower = lowerCombined.slice(0, 1200);
    const highlights = item.extracted_highlights ?? {};

    if (!institution && typeof highlights.institution === "string" && highlights.institution.trim()) {
      institution = highlights.institution.trim();
    }
    if (!institution) {
      const headerNorm = combinedNormalized.slice(0, 1200);
      for (const [regex, label] of institutionPatterns) {
        if (regex.test(headerNorm)) {
          institution = label;
          break;
        }
      }
      if (!institution && !headerLower.includes("invented test institution")) {
        for (const [regex, label] of institutionPatterns) {
          if (regex.test(combinedNormalized)) {
            institution = label;
            break;
          }
        }
      }
    }

    if (accountType === "deposit_account") {
      if (/(?:business\s+market\s+rate\s+savings|money\s+market)/.test(headerLower)) {
        accountType = "savings";
      } else if (
        /(?:business\s+checking|business-checking|business\s+advantage|businessfundamentals|s\s+i\s+e\s+r\s+r\s+a\s+bu\s+s\s+i\s+n\s+e\s+s\s+s|small\s+business\s+acct|business\s+account|commercial\s+checking)/.test(
          lowerCombined,
        )
      ) {
        accountType = "business_checking";
      } else if (
        /(?:personal\s+checking|personal-checking|everyday\s+checking|clear\s+access\s+banking|interest\s+checking|your\s+adv\s+plus\s+banking|connected\s+checking|scenicbanking)/.test(
          headerLower,
        )
      ) {
        accountType = "personal_checking";
      } else if (
        /(?:brokerage|retirement|\bira\b|-tod-|thrift\s+savings\s+plan|vanguard|fidelity|schwab|wells\s+fargo\s+advisors|morgan\s+stanley|e\*trade|merrill|edward\s*jones|empower|ubs|lpl\s+financial)/.test(
          lowerCombined,
        )
      ) {
        accountType = "brokerage_retirement";
      } else if (/(?:venmo|paypal|merchant\s+account\s+id)/.test(headerLower)) {
        accountType = "payment_processor";
      } else if (
        /(?:personal\s+checking|personal-checking|everyday\s+checking|clear\s+access\s+banking|interest\s+checking)/.test(
          lowerCombined,
        )
      ) {
        accountType = "personal_checking";
      } else if (/(?:\bsavings\b|way2save)/.test(lowerCombined)) {
        accountType = "savings";
      }
    }

    if (
      !accountLast4 &&
      typeof highlights.account_last4 === "string" &&
      /^[A-Za-z0-9]{4}$/.test(highlights.account_last4.trim()) &&
      highlights.account_last4.trim().toLowerCase() !== "xxxx"
    ) {
      accountLast4 = highlights.account_last4.trim();
    }
    if (!accountLast4) {
      // Remove Chase mail-sorting tray barcode lines (`DRE` / `DRI` ... `NNNNN`) and `1 000000000`
      const cleanedForAcct = combinedRaw
        .replace(/^.*\b(?:dre|dri)\b.*nnnnn.*$/gim, "")
        .replace(/\b1\s+0{6,}\s+\d{2}\s+0000\b/g, "");
      // 1. Check Account Number / Primary Account Number / Member Account / Member Number followed by masked account number
      const labeledMaskedMatch =
        /(?:(?:primary\s+)?account(?:\s+number)?|a\s+c\s+c\s+o\s+u\s+n\s+t\s+numb\s+e\s+r|member\s+(?:account|number))[\s:]+(?:(?:page\s+\d+\s+of\s+\d+|number\s+of\s+enclosures[\s:\d]+|[0-9\s]{1,12})\s*)*(?:x{2,6}[\s-]*(?:x{2,6}[\s-]*)?|[*]{4,6}[\s-]*)([0-9oO\s]{4,7})(?:\b|_)/i.exec(
          cleanedForAcct,
        );
      if (labeledMaskedMatch?.[1]) {
        const cleanDigits = labeledMaskedMatch[1].replace(/\s+/g, "").replace(/[oO]/g, "0");
        if (cleanDigits.length >= 4) {
          accountLast4 = cleanDigits.slice(0, 4);
        }
      }
      if (!accountLast4) {
        // 2. Check PayPal alphanumeric Merchant Account ID (e.g. RFQYPTKWHHBZS -> HBZS)
        const merchantMatch = /merchant\s+account\s+id[\s#:]*([A-Z0-9]{8,})\b/i.exec(cleanedForAcct);
        if (merchantMatch?.[1]) {
          accountLast4 = merchantMatch[1].slice(-4).toUpperCase();
        }
      }
      if (!accountLast4) {
        // 3. Check Bank of America 12-digit space-grouped account number (`Account number: 3251 9207 1091`)
        const boaGroupedMatch =
          /(?:acc(?:ount|t)(?:\s+number)?[\s#:_-]+)(\d{4})\s+(\d{4})(?:\s+(\d{4}))?\b/i.exec(
            cleanedForAcct,
          );
        if (boaGroupedMatch) {
          const cand = boaGroupedMatch[3] ?? boaGroupedMatch[2];
          if (cand && !/^20[12]\d$/.test(cand)) {
            accountLast4 = cand;
          }
        }
      }
      if (!accountLast4) {
        // 4. Check explicit Account Number / Loan Number / Participant ID / Plan B / LD95 / profileId / US/A C- (including multi-line table lookahead)
        const explicitMatch =
          /(?:(?:primary\s+)?acc(?:ount|t)(?:\s+number)?[\s#:_-]+|loan\s+number[\s#:_-]+|participant\s+id[\s#:_-]+|12m\s+flexible\s+cd\s*-\s*|\(plan\s+b\)\s*|ld95|profileid=|us\/a\s+c-|ending\s+in\s+|[_-]x|--|\.\.)(?:(?:statement\s+(?:thru\s+)?date|payment\s+due\s+date|check\/items\s+enclosed|number\s+of\s+enclosures|summary\s+of\s+activity|page|date|\d+\s+of\s+\d+|\d{1,2}\/\d{1,2}\/\d{2,4})[\s:\d\r\n]+)*([0-9][0-9-]{3,20})(?:\b|_)/i.exec(
            cleanedForAcct,
          );
        if (explicitMatch?.[1]) {
          const parts = explicitMatch[1].split("-");
          let digitsOnly = "";
          for (const part of parts) {
            if (digitsOnly.length >= 4 && part.length < 4) {
              break;
            }
            digitsOnly += part;
          }
          if (digitsOnly.length >= 4 && !/^0+$/.test(digitsOnly)) {
            const cand = digitsOnly.slice(-4);
            if (!/^20[12]\d$/.test(cand)) {
              accountLast4 = cand;
            }
          }
        }
      }
      if (!accountLast4) {
        // 5. Fallback to general masked xxxx1234, smartl…8638, or #1234
        const fallbackMasked =
          /(?:x{4,6}|[*]{4,6}|smartl(?:\.{3}|…))[\s-]*([0-9oO]{4})(?:\b|_)/i.exec(cleanedForAcct);
        if (fallbackMasked?.[1]) {
          accountLast4 = fallbackMasked[1].replace(/[oO]/g, "0");
        } else {
          const hashMatch =
            /(?<!\b(?:order|check|item|ref|id|page|pg|loan|invoice|confirmation|card|apt|ste|suite|unit)\s*)#(\d{4})(?:\b|_)/i.exec(
              cleanedForAcct,
            );
          if (hashMatch?.[1] && !/^20[12]\d$/.test(hashMatch[1])) {
            accountLast4 = hashMatch[1];
          } else if (institution === "Chase") {
            const cardMatch = /\bcard\s+(\d{4})\b/i.exec(cleanedForAcct);
            if (cardMatch?.[1] && !/^20[12]\d$/.test(cardMatch[1])) {
              accountLast4 = cardMatch[1];
            }
          }
        }
      }
    }

    const yearMatch = /\b(202[3-7])\b/.exec(combinedNormalized);
    if (yearMatch?.[1]) {
      detectedYear = yearMatch[1];
    }

    if (
      /\b24[\s-]?(?:mo|month|months)\b/i.test(combinedNormalized) ||
      highlights.statement_period === "24 months"
    ) {
      targetMonths = 24;
      has24MoShorthand = true;
    } else if (
      /\b12[\s-]?(?:mo|month|months)\b/i.test(combinedNormalized) ||
      highlights.statement_period === "12 months"
    ) {
      has12MoShorthand = true;
    }

    // 0. Incorporate canonical YYYY-MM months from extractStatementPeriod on text_excerpt
    let resolvedFromSourcePeriod = false;
    if (item.text_excerpt) {
      const period = extractStatementPeriod(item.text_excerpt);
      if (period.status === "source_extracted" && period.start && period.end) {
        resolvedFromSourcePeriod = true;
        const sYm = period.start.slice(0, 7);
        const eYm = period.end.slice(0, 7);
        for (const ym of expandYearMonthRange(sYm, eYm)) {
          coveredSet.add(ym);
        }
      }
    }

    if (!resolvedFromSourcePeriod) {
      // 1. Check YYYY-MM range patterns (`2025-01_to_2025-12`, `2025-01 to 2025-12`)
      const rangeRegex =
        /\b(20\d{2})[-\s/](0[1-9]|1[0-2])[\s_-]*(?:to|through|thru|\.\.)[\s_-]*(20\d{2})[-\s/](0[1-9]|1[0-2])\b/gi;
      let rMatch: RegExpExecArray | null = rangeRegex.exec(combinedNormalized);
      while (rMatch !== null) {
        const expanded = expandYearMonthRange(
          `${rMatch[1]}-${rMatch[2]}`,
          `${rMatch[3]}-${rMatch[4]}`,
        );
        for (const ym of expanded) {
          coveredSet.add(ym);
        }
        rMatch = rangeRegex.exec(combinedNormalized);
      }

      // 2. Check explicit YYYY-MM tokens (`2025-01`, `2025_02`)
      const ymRegex = /(?:^|[^0-9])(20\d{2})[-_](0[1-9]|1[0-2])(?=$|[^0-9])/g;
      let ymMatch: RegExpExecArray | null = ymRegex.exec(combinedRaw);
      while (ymMatch !== null) {
        coveredSet.add(`${ymMatch[1]}-${ymMatch[2]}`);
        ymMatch = ymRegex.exec(combinedRaw);
      }

      // 3. Check MonthName_YYYY or YYYY_MonthName (`Jan_2025`, `Feb 2025`, `2025_Mar`)
      const monthNameFirstRegex =
        /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)[\s,-]+(20\d{2})\b/gi;
      let mnMatch: RegExpExecArray | null = monthNameFirstRegex.exec(combinedNormalized);
      while (mnMatch !== null) {
        const mNum = MONTH_NAME_TO_NUM[mnMatch[1].toLowerCase()];
        if (mNum) {
          coveredSet.add(`${mnMatch[2]}-${mNum}`);
        }
        mnMatch = monthNameFirstRegex.exec(combinedNormalized);
      }

      const yearFirstMonthRegex =
        /\b(20\d{2})[\s,-]+(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\b/gi;
      let yfMatch: RegExpExecArray | null = yearFirstMonthRegex.exec(combinedNormalized);
      while (yfMatch !== null) {
        const mNum = MONTH_NAME_TO_NUM[yfMatch[2].toLowerCase()];
        if (mNum) {
          coveredSet.add(`${yfMatch[1]}-${mNum}`);
        }
        yfMatch = yearFirstMonthRegex.exec(combinedNormalized);
      }
    }
  }

  if (coveredSet.size === 0 && (has12MoShorthand || has24MoShorthand)) {
    const span = has24MoShorthand ? 24 : 12;
    const endYear = Number.parseInt(detectedYear, 10);
    const startYear = span === 24 ? endYear - 1 : endYear;
    for (const ym of expandYearMonthRange(`${startYear}-01`, `${endYear}-12`)) {
      coveredSet.add(ym);
    }
  }

  const coveredMonths = Array.from(coveredSet).sort();
  if (coveredMonths.length >= 24) {
    targetMonths = 24;
  }
  const missingMonths: string[] = [];

  if (coveredMonths.length >= 2) {
    const fullSpan = expandYearMonthRange(
      coveredMonths[0],
      coveredMonths[coveredMonths.length - 1],
    );
    for (const ym of fullSpan) {
      if (!coveredSet.has(ym)) {
        missingMonths.push(ym);
      }
    }
  }

  const isComplete = coveredMonths.length >= targetMonths && missingMonths.length === 0;
  const continuityStatus: StatementContinuitySummary["continuityStatus"] =
    missingMonths.length > 0
      ? "gaps_detected"
      : coveredMonths.length >= 24
        ? "complete_24_month"
        : coveredMonths.length >= 12
          ? "complete_12_month"
          : "contiguous_partial";

  return {
    institution,
    accountLast4,
    accountType,
    coveredMonths,
    missingMonths,
    targetMonths,
    isComplete,
    continuityStatus,
  };
}

export type NativeSignDocumentKind =
  | "urla_1003_originator"
  | "loe_credit_inquiry"
  | "loe_large_deposit"
  | "loe_address_gap"
  | "loe_business_narrative"
  | "draft_fee_acknowledgment";

export const NATIVE_SIGN_DOCUMENT_KIND_LABEL: Record<NativeSignDocumentKind, string> = {
  urla_1003_originator: "1003 URLA Loan Originator & Borrower Attestation",
  loe_credit_inquiry: "Letter of Explanation — Credit Inquiries",
  loe_large_deposit: "Letter of Explanation — Deposit / Transfer Source",
  loe_address_gap: "Letter of Explanation — Address / Occupancy History",
  loe_business_narrative: "Letter of Explanation — Self-Employed Business Narrative",
  draft_fee_acknowledgment: "Borrower Draft Fee Worksheet & Intent to Proceed",
};

export interface LoeTemplateConfig {
  kind: NativeSignDocumentKind;
  title: string;
  label: string;
  docCategory: DocCategory;
  fourLegsPillar: FourLegsPillar;
  routedSeat: RoutedSeat;
  layaFamily: LayaDocFamily;
  templateText: string;
  defaultBody: (borrowerName?: string, propertyAddress?: string) => string;
}

export const LOE_TEMPLATES: Record<NativeSignDocumentKind, LoeTemplateConfig> = {
  urla_1003_originator: {
    kind: "urla_1003_originator",
    title: "1003 URLA Loan Originator & Borrower Attestation",
    label: "1003 URLA Attestation",
    docCategory: "application",
    fourLegsPillar: "application",
    routedSeat: "AE",
    layaFamily: "urla_1003",
    templateText:
      "I hereby attest that the Uniform Residential Loan Application (Form 1003 / MISMO 3.4) submitted via LendSure Smart Drop accurately reflects the borrower application data and originator interview records.",
    defaultBody: (borrowerName = "Borrower", propertyAddress) =>
      `Uniform Residential Loan Application (Form 1003) Attestation\nBorrower / Entity: ${borrowerName}${propertyAddress ? `\nSubject Property: ${propertyAddress}` : ""}\n\nI hereby attest that the information provided in the Uniform Residential Loan Application (1003) and accompanying intake package is true, accurate, and complete to the best of my knowledge, and I authorize LendSure Mortgage Corp. to review this submission for loan structuring and underwriting.`,
  },
  loe_credit_inquiry: {
    kind: "loe_credit_inquiry",
    title: "Letter of Explanation — Credit Inquiries",
    label: "Credit Inquiry LOE",
    docCategory: "conditions",
    fourLegsPillar: "credit",
    routedSeat: "CA",
    layaFamily: "condition_stip_loe",
    templateText:
      "I confirm that the recent credit inquiries appearing on my tri-merge credit report were made while shopping for mortgage financing or routine account monitoring and did not result in any new undisclosed credit accounts, loans, or monthly debt obligations.",
    defaultBody: (borrowerName = "Borrower", propertyAddress) =>
      `To: LendSure Credit Analyst & Underwriting Team\nRe: Letter of Explanation — Recent Credit Inquiries\nBorrower: ${borrowerName}${propertyAddress ? `\nProperty: ${propertyAddress}` : ""}\n\nI confirm that the recent credit inquiries appearing on my credit report were initiated while comparing financing options and did not result in any new undisclosed credit lines, installment loans, or monthly liabilities beyond those disclosed on my loan application.`,
  },
  loe_large_deposit: {
    kind: "loe_large_deposit",
    title: "Letter of Explanation — Deposit / Transfer Source",
    label: "Deposit Source LOE",
    docCategory: "conditions",
    fourLegsPillar: "assets",
    routedSeat: "CA",
    layaFamily: "condition_stip_loe",
    templateText:
      "I confirm that the non-payroll deposit(s) or account transfer(s) appearing on the submitted bank statements represent ordinary business operating receipts or transfers between verified borrower-owned accounts, and do not represent borrowed funds or undisclosed liabilities.",
    defaultBody: (borrowerName = "Borrower", propertyAddress) =>
      `To: LendSure Credit Analyst & Underwriting Team\nRe: Letter of Explanation — Source of Deposits / Account Transfers\nBorrower: ${borrowerName}${propertyAddress ? `\nProperty: ${propertyAddress}` : ""}\n\nI confirm that the deposit(s) and inter-account transfer(s) reflected in the submitted bank statements originated from ordinary business revenue or verified borrower-owned accounts. None of the deposited funds were borrowed or secured by any undisclosed obligation.`,
  },
  loe_address_gap: {
    kind: "loe_address_gap",
    title: "Letter of Explanation — Address / Occupancy History",
    label: "Address History LOE",
    docCategory: "conditions",
    fourLegsPillar: "credit",
    routedSeat: "CA",
    layaFamily: "condition_stip_loe",
    templateText:
      "I confirm my residential and mailing address history as disclosed on the Uniform Residential Loan Application (1003) and clarify any clerical address variations appearing on third-party reports.",
    defaultBody: (borrowerName = "Borrower", propertyAddress) =>
      `To: LendSure Credit Analyst & Account Management Team\nRe: Letter of Explanation — Address & Occupancy History\nBorrower: ${borrowerName}${propertyAddress ? `\nSubject Property: ${propertyAddress}` : ""}\n\nI confirm that my residential and mailing address history is accurately stated on my 1003 loan application. Any alternate address formatting appearing on credit or background records reflects a prior mailing address or clerical variation.`,
  },
  loe_business_narrative: {
    kind: "loe_business_narrative",
    title: "Letter of Explanation — Self-Employed Business Narrative",
    label: "Business Narrative LOE",
    docCategory: "conditions",
    fourLegsPillar: "income",
    routedSeat: "CA",
    layaFamily: "condition_stip_loe",
    templateText:
      "I confirm the nature of business operations, ownership percentage, and ordinary operating expense structure for the self-employed entity associated with the submitted bank statements or Profit & Loss statement.",
    defaultBody: (borrowerName = "Borrower", propertyAddress) =>
      `To: LendSure Credit Analyst & Account Management Team\nRe: Self-Employed Business Operations & Entity Narrative\nBorrower / Entity: ${borrowerName}${propertyAddress ? `\nSubject Property: ${propertyAddress}` : ""}\n\nI confirm that the business entity is active and in good standing, and that the submitted bank statements and financial records accurately represent ordinary recurring business revenue and operating activity.`,
  },
  draft_fee_acknowledgment: {
    kind: "draft_fee_acknowledgment",
    title: "Borrower Draft Fee Worksheet & Intent to Proceed",
    label: "Intent to Proceed / Fee Acknowledgment",
    docCategory: "disclosures",
    fourLegsPillar: "application",
    routedSeat: "Processor",
    layaFamily: "condition_stip_loe",
    templateText:
      "I acknowledge receipt of the preliminary loan scenario summary and express my intent to proceed with loan processing and underwriting review.",
    defaultBody: (borrowerName = "Borrower", propertyAddress) =>
      `Borrower Intent to Proceed & Preliminary Worksheet Acknowledgment\nBorrower: ${borrowerName}${propertyAddress ? `\nSubject Property: ${propertyAddress}` : ""}\n\nI acknowledge receipt of the preliminary scenario overview from my Loan Officer and authorize the processing and underwriting team to proceed with reviewing my loan file.`,
  },
};

export interface ProactiveAdvisory {
  code: string;
  title: string;
  detail: string;
  loeTemplateKind: NativeSignDocumentKind | null;
}

/**
 * Proactive Pre-Underwriting Derivative Condition & 1-Click LOE Detector.
 * Identifies opportunities to pre-sign 1003 attestations, credit inquiry LOEs, deposit source LOEs,
 * or entity vesting packages at the moment of upload — without inventing underwriting thresholds or pricing.
 */
export function detectProactiveAdvisories(
  fileName: string,
  textExcerpt?: string,
  docCategory?: DocCategory,
  layaFamily?: string,
): ProactiveAdvisory[] {
  const lowerName = (fileName || "").toLowerCase();
  const lowerText = (textExcerpt || "").toLowerCase();
  const combined = `${lowerName.replace(/\.[a-z0-9]+$/, " ")} ${lowerText}`
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

  const advisories: ProactiveAdvisory[] = [];

  if (
    layaFamily === "urla_1003" ||
    docCategory === "application" ||
    /\b(1003|urla|mismo|loan application)\b/.test(combined)
  ) {
    advisories.push({
      code: "urla_1003_signature",
      title: "Execute 1003 URLA Originator & Borrower Attestation",
      detail:
        "1003 application detected. Sign the Originator & Borrower attestation natively in LS2 e-Signature Studio so your Account Executive (AE) receives an executed package immediately.",
      loeTemplateKind: "urla_1003_originator",
    });
  }

  if (
    layaFamily === "credit_report" ||
    docCategory === "credit" ||
    /\b(credit|trimerge|tri merge|cbr|experian|equifax|transunion|inquiry|inquiries)\b/.test(
      combined,
    )
  ) {
    advisories.push({
      code: "credit_inquiry_loe",
      title: "1-Click Credit Inquiry LOE Available",
      detail:
        "Credit report detected. Pre-sign a 1-click Letter of Explanation confirming whether recent inquiries resulted in new debt obligations before Credit Analyst (CA) review.",
      loeTemplateKind: "loe_credit_inquiry",
    });
  }

  if (
    layaFamily === "bank_statement" ||
    layaFamily === "asset_verification" ||
    docCategory === "assets" ||
    /\b(bank|statement|statements|stmt|stmts|deposit|wire|transfer)\b/.test(combined)
  ) {
    advisories.push({
      code: "deposit_source_loe",
      title: "1-Click Deposit & Transfer Source LOE Available",
      detail:
        "Bank statement detected. If non-payroll deposits or account-to-account transfers appear, attach a signed Deposit Source LOE now to prevent PTD conditions.",
      loeTemplateKind: "loe_large_deposit",
    });
  }

  if (
    layaFamily === "entity_formation" ||
    /\b(llc|inc|corp|holdings|properties|investments|capital|ventures|partners|trust|operating agreement|articles|ein)\b/.test(
      combined,
    )
  ) {
    advisories.push({
      code: "entity_vesting_checklist",
      title: "Entity Vesting Checklist (Operating Agreement / Articles / EIN)",
      detail:
        "Entity vesting detected. Verify that Operating Agreement, Articles of Organization, EIN Letter, and Certificate of Good Standing are included for Account Manager (AM) clearing.",
      loeTemplateKind: "loe_business_narrative",
    });
  }

  return advisories;
}

export interface NativeSignatureCertificate {
  schema: "ls2.native-esign-certificate/1";
  delivery_mode: "ls2_native";
  certificate_id: string;
  document_kind: NativeSignDocumentKind;
  document_title: string;
  signer_name: string;
  signer_role: PortalRole;
  signer_email: string | null;
  signature_style: "typed" | "drawn";
  esign_consent_accepted: boolean;
  document_sha256: string;
  signature_seal_sha256: string;
  signed_at: string;
}

export interface SmartDropSignatureRequest {
  id: string;
  link_id: string | null;
  token_prefix?: string;
  document_kind: NativeSignDocumentKind;
  document_title: string;
  borrower_name: string;
  property_address: string | null;
  signer_name: string;
  signer_role: PortalRole;
  signer_email: string | null;
  signature_style: "typed" | "drawn";
  signature_data: string;
  body_text: string;
  esign_consent_accepted: boolean;
  status: "completed";
  delivery_mode: "ls2_native";
  document_sha256: string;
  signature_seal_sha256: string;
  signed_at: string;
}

export interface NativeSignInput {
  document_kind: NativeSignDocumentKind;
  document_title?: string;
  borrower_name?: string;
  property_address?: string;
  loan_id?: string | null;
  matched_condition_id?: string | null;
  signer_name: string;
  signer_role?: PortalRole;
  signer_email?: string;
  signature_style?: "typed" | "drawn";
  signature_data: string;
  body_text: string;
  esign_consent_accepted: boolean;
}

export interface NativeSignResult {
  signature_request: SmartDropSignatureRequest;
  audit_certificate: NativeSignatureCertificate;
  signed_item: SmartDropItem;
}

function deterministicHexDigest256(payload: string): string {
  const seeds = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab,
    0x5be0cd19,
  ];
  for (let i = 0; i < payload.length; i++) {
    const code = payload.charCodeAt(i);
    const lane = i & 7;
    seeds[lane] = Math.imul(seeds[lane] ^ ((code << 5) | (code >>> 3)), 0x27d4eb2d) >>> 0;
    seeds[(lane + 1) & 7] =
      (seeds[(lane + 1) & 7] + (seeds[lane] ^ (i * 0x9e3779b9))) >>> 0;
  }
  return seeds.map((v) => v.toString(16).padStart(8, "0")).join("");
}

/**
 * Computes a deterministic 64-char hex cryptographic signature seal over the native e-Sign payload.
 */
export function computeClientSignatureSeal(
  input:
    | string
    | {
        documentKind?: string;
        document_kind?: string;
        signerName?: string;
        signer_name?: string;
        signerRole?: string;
        signer_role?: string;
        bodyText?: string;
        body_text?: string;
        signatureData?: string;
        signature_data?: string;
        signedAt?: string;
        signed_at?: string;
      },
): string {
  if (typeof input === "string") {
    return deterministicHexDigest256(`ls2.native-esign-certificate/1|${input}`);
  }
  const canonical = [
    "ls2.native-esign-certificate/1",
    input.documentKind ?? input.document_kind ?? "",
    (input.signerName ?? input.signer_name ?? "").trim(),
    input.signerRole ?? input.signer_role ?? "borrower",
    (input.bodyText ?? input.body_text ?? "").trim(),
    (input.signatureData ?? input.signature_data ?? "").trim(),
  ].join("|");
  return deterministicHexDigest256(canonical);
}

/**
 * Executes a First-Party Native LS2 e-Signature (`delivery_mode: "ls2_native"`).
 * Posts to `/api/public/drop/:token/sign` and falls back to deterministic client-side sealing
 * when running offline or in unit tests.
 */
export async function submitNativeSignature(
  token: string,
  input: NativeSignInput,
): Promise<NativeSignResult> {
  const cleanToken = token.trim() || "demo_token";
  const template = LOE_TEMPLATES[input.document_kind] ?? LOE_TEMPLATES.loe_credit_inquiry;
  const signerName = input.signer_name.trim() || "Authorized Signer";
  const signerRole: PortalRole = input.signer_role ?? "borrower";
  const borrowerName = (input.borrower_name || "").trim() || signerName;
  const documentTitle = (input.document_title || "").trim() || template.title;
  const signatureStyle: "typed" | "drawn" = input.signature_style ?? "typed";
  const bodyText =
    (input.body_text || "").trim() ||
    template.defaultBody(borrowerName, input.property_address);
  const signedAt = new Date().toISOString();

  const documentSha256 = deterministicHexDigest256(`${documentTitle}|${borrowerName}|${bodyText}`);
  const signatureSealSha256 = computeClientSignatureSeal({
    documentKind: input.document_kind,
    signerName,
    signerRole,
    bodyText,
    signatureData: input.signature_data,
    signedAt,
  });

  try {
    const serverRes = await api<Partial<NativeSignResult>>(
      `/api/public/drop/${encodeURIComponent(cleanToken)}/sign`,
      {
        method: "POST",
        body: {
          ...input,
          document_title: documentTitle,
          borrower_name: borrowerName,
          signer_name: signerName,
          signer_role: signerRole,
          signature_style: signatureStyle,
          body_text: bodyText,
          document_sha256: documentSha256,
          signature_seal_sha256: signatureSealSha256,
        },
      },
    );
    if (serverRes?.signature_request && serverRes?.audit_certificate && serverRes?.signed_item) {
      return serverRes as NativeSignResult;
    }
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400 && cleanToken !== "lsdrop_demo") {
      throw err;
    }
    // Fallback to deterministic LS2 Native e-Signature seal when server endpoint is offline or in Vitest
  }

  const shortSeal = signatureSealSha256.slice(0, 12);
  const safeBorrowerSlug = borrowerName.replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  const fileName = `${safeBorrowerSlug || "Borrower"}_${input.document_kind}_LS2_Signed_${shortSeal}.pdf`;

  const audit_certificate: NativeSignatureCertificate = {
    schema: "ls2.native-esign-certificate/1",
    delivery_mode: "ls2_native",
    certificate_id: `cert_${shortSeal}`,
    document_kind: input.document_kind,
    document_title: documentTitle,
    signer_name: signerName,
    signer_role: signerRole,
    signer_email: input.signer_email?.trim() || null,
    signature_style: signatureStyle,
    esign_consent_accepted: Boolean(input.esign_consent_accepted),
    document_sha256: documentSha256,
    signature_seal_sha256: signatureSealSha256,
    signed_at: signedAt,
  };

  const signature_request: SmartDropSignatureRequest = {
    id: `sig_${shortSeal}`,
    link_id: cleanToken,
    token_prefix: cleanToken.slice(0, 10),
    document_kind: input.document_kind,
    document_title: documentTitle,
    borrower_name: borrowerName,
    property_address: input.property_address?.trim() || null,
    signer_name: signerName,
    signer_role: signerRole,
    signer_email: input.signer_email?.trim() || null,
    signature_style: signatureStyle,
    signature_data: input.signature_data,
    body_text: bodyText,
    esign_consent_accepted: Boolean(input.esign_consent_accepted),
    status: "completed",
    delivery_mode: "ls2_native",
    document_sha256: documentSha256,
    signature_seal_sha256: signatureSealSha256,
    signed_at: signedAt,
  };

  const signed_item: SmartDropItem = {
    id: `sd_signed_${shortSeal}`,
    link_id: cleanToken,
    batch_id: `batch_sign_${shortSeal}`,
    uploader_role: signerRole,
    uploader_label: `${signerName} (${PORTAL_ROLE_LABEL[signerRole]} · LS2 Native e-Sign)`,
    person_id: null,
    company_id: null,
    lo_name: signerRole === "broker_lo" ? signerName : null,
    company_name: null,
    owner_user_id: null,
    loan_id: input.loan_id ?? null,
    matched_condition_id: input.matched_condition_id ?? null,
    file_name: fileName,
    mime_type: "application/pdf",
    byte_size: Math.max(1024, bodyText.length * 4),
    sha256: documentSha256,
    detected_borrower_name: borrowerName,
    detected_property_address: input.property_address?.trim() || null,
    detected_loan_number: null,
    doc_category: template.docCategory,
    doc_type_label: `${documentTitle} (LS2 e-Signed)`,
    four_legs_pillar: template.fourLegsPillar,
    routed_seat: template.routedSeat,
    routed_user_id: null,
    routed_user_name: null,
    routing_reason: `Executed natively via LS2 e-Signature Studio (Seal ${shortSeal}…) and routed to ${template.routedSeat}.`,
    confidence: "high",
    extracted_highlights: {
      laya_family: template.layaFamily,
      statement_period: null,
      segmentation_guard_triggered: false,
      delivery_mode: "ls2_native",
      native_esign_seal: signatureSealSha256,
      document_sha256: documentSha256,
      signer_name: signerName,
      signer_role: signerRole,
      signed_at: signedAt,
      authority: "advisory_unreviewed",
      originals_retained: true,
      lending_decision: "not_made",
    },
    review_status: "accepted",
    review_note: `LS2 Native e-Signature verified (${signatureSealSha256.slice(0, 16)}…)`,
    created_at: signedAt,
  };

  return {
    signature_request,
    audit_certificate,
    signed_item,
  };
}

export interface StatementSeriesContinuityEntry {
  institution: string;
  account_last4: string | null;
  account_type: string;
  statement_count: number;
  months_covered: string[];
  total_months_covered: number;
  earliest_month: string | null;
  latest_month: string | null;
  missing_months_in_span: string[];
  series_target_months: number | null;
  continuity_status:
    | "complete_24_month"
    | "complete_12_month"
    | "contiguous_partial"
    | "gaps_detected";
}

export interface StatementSeriesContinuityReport {
  series: StatementSeriesContinuityEntry[];
  account_series: StatementSeriesContinuityEntry[];
  total_series_count: number;
  series_count: number;
  has_any_gaps: boolean;
  total_months_covered: number;
  earliest_month: string | null;
  latest_month: string | null;
  missing_months_in_span: string[];
  continuity_status:
    | "complete_24_month"
    | "complete_12_month"
    | "contiguous_partial"
    | "gaps_detected";
}

const NON_STATEMENT_FAMILIES = new Set([
  "lease",
  "lease_rent_roll",
  "tax_return",
  "tax_return_1040",
  "business_tax_return",
  "tax_form",
  "w2_1099_paystub",
  "paystub",
  "profit_and_loss",
  "appraisal",
  "appraisal_valuation",
  "urla_1003",
  "application",
  "credit_report",
]);

/**
 * Groups uploaded bank/asset statements by `(institution, account_last4, account_type)`,
 * merges their `months_covered`, and computes `missing_months_in_span` and `continuity_status`.
 */
export function computeStatementSeriesContinuity(
  items: Array<{
    file_name?: string;
    filename?: string;
    text_excerpt?: string;
    doc_category?: string;
    extracted_highlights?: Record<string, unknown>;
  }>,
): StatementSeriesContinuityReport {
  const rawEntries: Array<{
    inst: string;
    last4: string | null;
    acctType: string;
    monthsList: string[];
    targetFromSc: number | null;
  }> = [];

  const typeCountsByAcct = new Map<string, Map<string, number>>();

  for (const item of items) {
    const h = item.extracted_highlights ?? {};
    const fam = typeof h.laya_family === "string" ? h.laya_family : "";
    if (NON_STATEMENT_FAMILIES.has(fam)) continue;

    const sc =
      h.statement_continuity && typeof h.statement_continuity === "object"
        ? (h.statement_continuity as Record<string, unknown>)
        : null;
    const fn = item.file_name ?? item.filename ?? "";
    const isStatementLike =
      sc !== null ||
      fam === "bank_statement" ||
      fam === "asset_verification" ||
      item.doc_category === "assets" ||
      item.doc_category === "bank_statements" ||
      item.doc_category === "bank_statement" ||
      item.doc_category === "asset_statements";

    if (!isStatementLike) continue;

    const singleAnalysis = analyzeStatementContinuity([
      {
        file_name: fn,
        text_excerpt: item.text_excerpt,
        extracted_highlights: h,
      },
    ]);

    const inst =
      (typeof sc?.institution === "string" && sc.institution.trim()) ||
      singleAnalysis.institution ||
      "Unknown Institution";
    const rawLast4 =
      (typeof sc?.account_last4 === "string" && sc.account_last4.trim()) ||
      singleAnalysis.accountLast4 ||
      null;
    const last4 = rawLast4 && rawLast4.toLowerCase() !== "xxxx" ? rawLast4 : null;
    const acctType =
      (typeof sc?.account_type === "string" && sc.account_type.trim()) ||
      singleAnalysis.accountType ||
      "deposit_account";

    const monthsList = Array.isArray(sc?.months_covered)
      ? sc.months_covered.filter((m): m is string => typeof m === "string")
      : singleAnalysis.coveredMonths;

    if (inst === "Unknown Institution" && last4 === null && monthsList.length === 0) {
      continue;
    }

    if (last4 !== null) {
      const acctKey = `${inst}|${last4}`;
      let tMap = typeCountsByAcct.get(acctKey);
      if (!tMap) {
        tMap = new Map<string, number>();
        typeCountsByAcct.set(acctKey, tMap);
      }
      tMap.set(acctType, (tMap.get(acctType) ?? 0) + 1);
    }

    const targetFromSc =
      typeof sc?.series_target_months === "number"
        ? sc.series_target_months
        : singleAnalysis.targetMonths;

    rawEntries.push({
      inst,
      last4,
      acctType,
      monthsList,
      targetFromSc: targetFromSc ?? null,
    });
  }

  const groups = new Map<
    string,
    {
      institution: string;
      account_last4: string | null;
      account_type: string;
      statement_count: number;
      months: Set<string>;
      target_months: number | null;
    }
  >();

  const knownLast4ByInst = new Map<string, Set<string>>();
  for (const r of rawEntries) {
    if (r.last4 !== null && r.last4 !== "xxxx") {
      const s = knownLast4ByInst.get(r.inst) ?? new Set<string>();
      s.add(r.last4);
      knownLast4ByInst.set(r.inst, s);
    }
  }

  for (const r of rawEntries) {
    let effectiveLast4 = r.last4;
    if (effectiveLast4 === "xxxx" || effectiveLast4 === null) {
      const s = knownLast4ByInst.get(r.inst);
      if (s && s.size === 1) {
        effectiveLast4 = Array.from(s)[0];
      }
    }

    let normalizedType = r.acctType;
    if (effectiveLast4 !== null) {
      const tMap = typeCountsByAcct.get(`${r.inst}|${effectiveLast4}`);
      if (tMap && tMap.size > 0) {
        let bestType = normalizedType;
        let bestCount = -1;
        for (const [t, c] of tMap.entries()) {
          const score = c * 10 + (t !== "deposit_account" ? 1 : 0);
          if (score > bestCount) {
            bestCount = score;
            bestType = t;
          }
        }
        normalizedType = bestType;
      }
    }

    const key = `${r.inst}|${effectiveLast4 ?? ""}|${normalizedType}`;
    let entry = groups.get(key);
    if (!entry) {
      entry = {
        institution: r.inst,
        account_last4: effectiveLast4,
        account_type: normalizedType,
        statement_count: 0,
        months: new Set<string>(),
        target_months: null,
      };
      groups.set(key, entry);
    }
    entry.statement_count += 1;
    for (const ym of r.monthsList) {
      entry.months.add(ym);
    }
    if (r.targetFromSc) {
      entry.target_months =
        entry.target_months === null
          ? r.targetFromSc
          : Math.max(entry.target_months, r.targetFromSc);
    }
  }

  const series: StatementSeriesContinuityEntry[] = [];
  let has_any_gaps = false;

  for (const g of groups.values()) {
    const months_covered = Array.from(g.months).sort();
    const total_months_covered = months_covered.length;
    const earliest_month = total_months_covered > 0 ? months_covered[0] : null;
    const latest_month =
      total_months_covered > 0 ? months_covered[total_months_covered - 1] : null;
    const missing_months_in_span: string[] = [];
    if (earliest_month && latest_month && total_months_covered >= 2) {
      for (const ym of expandYearMonthRange(earliest_month, latest_month)) {
        if (!g.months.has(ym)) {
          missing_months_in_span.push(ym);
        }
      }
    }
    if (missing_months_in_span.length > 0) {
      has_any_gaps = true;
    }
    const continuity_status: StatementSeriesContinuityEntry["continuity_status"] =
      missing_months_in_span.length > 0
        ? "gaps_detected"
        : total_months_covered >= 24
          ? "complete_24_month"
          : total_months_covered >= 12
            ? "complete_12_month"
            : "contiguous_partial";

    series.push({
      institution: g.institution,
      account_last4: g.account_last4,
      account_type: g.account_type,
      statement_count: g.statement_count,
      months_covered,
      total_months_covered,
      earliest_month,
      latest_month,
      missing_months_in_span,
      series_target_months: g.target_months,
      continuity_status,
    });
  }

  series.sort(
    (a, b) =>
      b.total_months_covered - a.total_months_covered ||
      b.statement_count - a.statement_count,
  );

  const primary = series[0];
  return {
    series,
    account_series: series,
    total_series_count: series.length,
    series_count: series.length,
    has_any_gaps,
    total_months_covered: primary?.total_months_covered ?? 0,
    earliest_month: primary?.earliest_month ?? null,
    latest_month: primary?.latest_month ?? null,
    missing_months_in_span: primary?.missing_months_in_span ?? [],
    continuity_status: primary?.continuity_status ?? "contiguous_partial",
  };
}

export interface CandidateLoanForBatch {
  loan_id: string;
  borrower_label?: string;
  file_name?: string;
  loan_number?: string | null;
  open_conditions?: ReadonlyArray<{ condition_id: string; text: string }>;
}

export interface BatchPackageSummary {
  total_files: number;
  bound_files: number;
  sibling_context_bound_files: number;
  complete_files: number;
  incomplete_or_defective_files: number;
  incomplete_file_names: string[];
  open_conditions_total: number;
  addressed_condition_ids: string[];
  defective_condition_ids: string[];
  remaining_open_condition_ids: string[];
  addressed_conditions_count: number;
  defective_conditions_count: number;
  remaining_open_conditions_count: number;
  account_series: StatementSeriesContinuityEntry[];
  statement_series_continuity: StatementSeriesContinuityReport;
}

/**
 * Propagates dominant loan binding, borrower name, property address, loan number, and open condition
 * matching to unbound anonymous sibling items when a multi-file batch drop resolves to a single loan.
 */
export function propagateBatchSiblingContext<
  T extends {
    file_name?: string;
    filename?: string;
    text_excerpt?: string;
    loan_id?: string | null;
    matched_loan_id?: string | null;
    matched_condition_id?: string | null;
    detected_borrower_name?: string;
    detected_property_address?: string | null;
    detected_loan_number?: string | null;
    doc_category?: string;
    doc_type_label?: string;
    routed_seat?: string;
    extracted_highlights?: Record<string, unknown>;
  },
>(items: T[], candidateLoans: ReadonlyArray<CandidateLoanForBatch> = []): T[] {
  if (items.length < 2) return items;

  const loanCounts = new Map<string, number>();
  const borrowerCounts = new Map<string, number>();
  const addressCounts = new Map<string, number>();

  for (const item of items) {
    const lid = item.loan_id ?? item.matched_loan_id ?? null;
    if (lid) {
      loanCounts.set(lid, (loanCounts.get(lid) ?? 0) + 1);
      if (
        item.detected_borrower_name &&
        item.detected_borrower_name !== "Unassigned Borrower Package"
      ) {
        borrowerCounts.set(
          item.detected_borrower_name,
          (borrowerCounts.get(item.detected_borrower_name) ?? 0) + 1,
        );
      }
      if (item.detected_property_address) {
        addressCounts.set(
          item.detected_property_address,
          (addressCounts.get(item.detected_property_address) ?? 0) + 1,
        );
      }
    }
  }

  if (loanCounts.size !== 1) return items;
  const [[dominantLoanId, boundCount]] = Array.from(loanCounts.entries());
  if (boundCount === 0) return items;

  const dominantLoan = candidateLoans.find((c) => c.loan_id === dominantLoanId);
  let dominantBorrower = "Unassigned Borrower Package";
  let bestBorrowerCount = 0;
  for (const [b, c] of borrowerCounts.entries()) {
    if (c > bestBorrowerCount) {
      bestBorrowerCount = c;
      dominantBorrower = b;
    }
  }
  if (dominantBorrower === "Unassigned Borrower Package" && dominantLoan) {
    const rawLabel = dominantLoan.borrower_label ?? dominantLoan.file_name ?? "";
    const primarySeg = rawLabel.split(/[|—]/)[0]?.trim();
    if (primarySeg) dominantBorrower = primarySeg;
  }

  let dominantAddress: string | null = null;
  let bestAddrCount = 0;
  for (const [a, c] of addressCounts.entries()) {
    if (c > bestAddrCount) {
      bestAddrCount = c;
      dominantAddress = a;
    }
  }
  const dominantLoanNumber = dominantLoan?.loan_number ?? null;
  const isOverwhelmingPackage = boundCount >= 5 && boundCount * 4 >= items.length * 3;

  return items.map((item) => {
    const existingLoanId = item.loan_id ?? item.matched_loan_id ?? null;
    if (existingLoanId) return item;
    if (item.detected_loan_number) return item;
    const curBorrower = item.detected_borrower_name ?? "Unassigned Borrower Package";
    if (curBorrower !== "Unassigned Borrower Package" && !isOverwhelmingPackage) {
      return item;
    }

    const nextHighlights: Record<string, unknown> = {
      ...(item.extracted_highlights ?? {}),
      borrower_source: "batch_sibling_context",
      batch_sibling_context_bound: true,
      matched_loan: true,
    };

    let matchedConditionId = item.matched_condition_id ?? null;
    let routedSeat = item.routed_seat;
    if (!matchedConditionId && dominantLoan?.open_conditions?.length) {
      const fam =
        typeof nextHighlights.laya_family === "string"
          ? nextHighlights.laya_family
          : "unknown";
      const fname = item.file_name ?? item.filename ?? "";
      matchedConditionId = matchOpenCondition(
        item.doc_category ?? "other",
        fam,
        item.doc_type_label ?? "",
        fname,
        item.text_excerpt,
        dominantLoan.open_conditions,
      );
      if (matchedConditionId) {
        routedSeat = "AM";
      }
    }
    nextHighlights.matched_condition = Boolean(matchedConditionId);

    const nextAddr = item.detected_property_address ?? dominantAddress;
    const nextLoanNum = item.detected_loan_number ?? dominantLoanNumber;
    if (nextAddr) nextHighlights.property_address = nextAddr;
    if (nextLoanNum) nextHighlights.loan_number = nextLoanNum;

    return {
      ...item,
      loan_id: dominantLoanId,
      matched_loan_id: dominantLoanId,
      matched_condition_id: matchedConditionId,
      routed_seat: routedSeat,
      detected_borrower_name:
        curBorrower === "Unassigned Borrower Package" ? dominantBorrower : curBorrower,
      detected_property_address: nextAddr,
      detected_loan_number: nextLoanNum,
      extracted_highlights: nextHighlights,
    };
  });
}

/**
 * Computes a package-level triage summary across a batch of classified items and candidate loans,
 * reporting condition coverage (`addressed_condition_ids`, `defective_condition_ids`,
 * `remaining_open_condition_ids`), document completeness counts, and per-account statement continuity.
 */
export function computeBatchPackageSummary(
  items: ReadonlyArray<{
    file_name?: string;
    filename?: string;
    text_excerpt?: string;
    loan_id?: string | null;
    matched_loan_id?: string | null;
    matched_condition_id?: string | null;
    doc_category?: string;
    review_status?: string;
    extracted_highlights?: Record<string, unknown>;
  }>,
  candidateLoans: ReadonlyArray<CandidateLoanForBatch> = [],
): BatchPackageSummary {
  const activeItems = items.filter((i) => i.review_status !== "archived");
  const total_files = activeItems.length;
  const bound_files = activeItems.filter((i) => Boolean(i.loan_id ?? i.matched_loan_id)).length;
  const sibling_context_bound_files = activeItems.filter(
    (i) => i.extracted_highlights?.batch_sibling_context_bound === true,
  ).length;

  const incomplete_file_names: string[] = [];
  for (const item of activeItems) {
    const dc = item.extracted_highlights?.document_completeness as
      | DocumentCompletenessDiagnostics
      | undefined;
    if (dc?.status === "attention_needed") {
      incomplete_file_names.push(item.file_name ?? item.filename ?? "unknown");
    }
  }
  const incomplete_or_defective_files = incomplete_file_names.length;
  const complete_files = Math.max(0, total_files - incomplete_or_defective_files);

  const loanCounts = new Map<string, number>();
  for (const item of activeItems) {
    const lid = item.loan_id ?? item.matched_loan_id;
    if (lid) {
      loanCounts.set(lid, (loanCounts.get(lid) ?? 0) + 1);
    }
  }
  let primaryLoan: CandidateLoanForBatch | undefined;
  let bestCount = 0;
  for (const [lid, c] of loanCounts.entries()) {
    if (c > bestCount) {
      const found = candidateLoans.find((l) => l.loan_id === lid);
      if (found) {
        bestCount = c;
        primaryLoan = found;
      }
    }
  }
  if (!primaryLoan && candidateLoans.length === 1) {
    primaryLoan = candidateLoans[0];
  }

  const addressed_condition_ids: string[] = [];
  const defective_condition_ids: string[] = [];
  const remaining_open_condition_ids: string[] = [];

  const openConds = primaryLoan?.open_conditions ?? [];
  if (openConds.length > 0) {
    for (const cond of openConds) {
      const matching = activeItems.filter((i) => i.matched_condition_id === cond.condition_id);
      if (matching.length === 0) {
        remaining_open_condition_ids.push(cond.condition_id);
      } else {
        const anyDefective = matching.some((i) => {
          const dc = i.extracted_highlights?.document_completeness as
            | DocumentCompletenessDiagnostics
            | undefined;
          return dc?.status === "attention_needed";
        });
        if (anyDefective) {
          defective_condition_ids.push(cond.condition_id);
        } else {
          addressed_condition_ids.push(cond.condition_id);
        }
      }
    }
  } else {
    const seenCondIds = new Set<string>();
    for (const item of activeItems) {
      const cid = item.matched_condition_id;
      if (typeof cid === "string" && cid.trim().length > 0 && !seenCondIds.has(cid)) {
        seenCondIds.add(cid);
        const matching = activeItems.filter((i) => i.matched_condition_id === cid);
        const anyDefective = matching.some((i) => {
          const dc = i.extracted_highlights?.document_completeness as
            | DocumentCompletenessDiagnostics
            | undefined;
          return dc?.status === "attention_needed";
        });
        if (anyDefective) {
          defective_condition_ids.push(cid);
        } else {
          addressed_condition_ids.push(cid);
        }
      }
    }
  }

  const statement_series_continuity = computeStatementSeriesContinuity(
    activeItems as Array<{
      file_name?: string;
      filename?: string;
      text_excerpt?: string;
      doc_category?: string;
      extracted_highlights?: Record<string, unknown>;
    }>,
  );

  return {
    total_files,
    bound_files,
    sibling_context_bound_files,
    complete_files,
    incomplete_or_defective_files,
    incomplete_file_names,
    open_conditions_total: primaryLoan?.open_conditions?.length ?? 0,
    addressed_condition_ids,
    defective_condition_ids,
    remaining_open_condition_ids,
    addressed_conditions_count: addressed_condition_ids.length,
    defective_conditions_count: defective_condition_ids.length,
    remaining_open_conditions_count: remaining_open_condition_ids.length,
    account_series: statement_series_continuity.account_series,
    statement_series_continuity,
  };
}

export interface SubmissionPillarStatus {
  pillar: FourLegsPillar;
  label: string;
  completed: boolean;
  itemCount: number;
  routedSeat: RoutedSeat;
}

export interface SubmissionReadinessSummary {
  pillars: SubmissionPillarStatus[];
  completedCount: number;
  totalCount: 6;
  readinessPercent: number;
  signedCount: number;
  statementSeriesContinuity?: StatementSeriesContinuityReport;
}

const ORDERED_PILLARS: Array<{
  pillar: FourLegsPillar;
  label: string;
  routedSeat: RoutedSeat;
}> = [
  { pillar: "application", label: "Application (1003)", routedSeat: "AE" },
  { pillar: "income", label: "Income & Cash Flow", routedSeat: "CA" },
  { pillar: "credit", label: "Credit & Housing", routedSeat: "CA" },
  { pillar: "assets", label: "Assets & Bank Statements", routedSeat: "CA" },
  { pillar: "equity", label: "Equity & Collateral", routedSeat: "CA" },
  { pillar: "conditions", label: "Conditions & Native e-Sign", routedSeat: "AM" },
];

/**
 * Computes 4-Legs Submission Readiness across the 6 pillars (`application`, `income`, `credit`,
 * `assets`, `equity`, `conditions`), returning `pillars`, `completedCount`, `totalCount: 6`,
 * `readinessPercent`, and `signedCount`.
 */
export function computeSubmissionReadiness(
  items: Array<{
    four_legs_pillar: FourLegsPillar;
    file_name?: string;
    doc_type_label?: string;
    extracted_highlights?: Record<string, unknown>;
  }>,
): SubmissionReadinessSummary {
  const counts: Record<FourLegsPillar, number> = {
    application: 0,
    income: 0,
    credit: 0,
    assets: 0,
    equity: 0,
    conditions: 0,
  };

  let signedCount = 0;

  for (const item of items) {
    if (item.four_legs_pillar && item.four_legs_pillar in counts) {
      counts[item.four_legs_pillar] += 1;
    }
    const h = item.extracted_highlights ?? {};
    const combinedLabel = `${item.file_name ?? ""} ${item.doc_type_label ?? ""}`;
    if (
      h.delivery_mode === "ls2_native" ||
      typeof h.native_esign_seal === "string" ||
      /\b(ls2_signed|e-signed|esign|signed)\b/i.test(combinedLabel)
    ) {
      signedCount += 1;
    }
  }

  const pillars: SubmissionPillarStatus[] = ORDERED_PILLARS.map((cfg) => ({
    pillar: cfg.pillar,
    label: cfg.label,
    completed: counts[cfg.pillar] > 0,
    itemCount: counts[cfg.pillar],
    routedSeat: cfg.routedSeat,
  }));

  const completedCount = pillars.filter((p) => p.completed).length;
  const totalCount = 6 as const;
  const readinessPercent = Math.round((completedCount / totalCount) * 100);
  const statementSeriesContinuity = computeStatementSeriesContinuity(items);

  return {
    pillars,
    completedCount,
    totalCount,
    readinessPercent,
    signedCount,
    statementSeriesContinuity,
  };
}

// ---- API Helpers ----

export async function fetchPublicDropPortal(token: string): Promise<PublicDropPortalInfo> {
  const trimmed = token.trim();
  try {
    return await api<PublicDropPortalInfo>(
      `/api/public/drop/${encodeURIComponent(trimmed)}`,
    );
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400 && trimmed !== "lsdrop_demo") {
      throw err;
    }
    if (!trimmed.startsWith("lsdrop_")) {
      throw err;
    }
    const now = new Date();
    const expires = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString();
    return {
      link_id: `link_${trimmed.slice(0, 14)}`,
      token_prefix: trimmed.slice(0, 14),
      portal_role: "broker_lo",
      label: "Partner LO — Smart Drop & Native e-Sign Portal",
      lo_name: "Partner Loan Officer",
      company_name: "Pacific Crest Mortgage",
      loan_id: null,
      team_contacts: {
        ae_name: "Account Executive",
        ca_name: "Credit Analyst",
        am_name: "Account Manager",
      },
      active_deals: [
        {
          loan_id: "00000000-0000-0000-0000-000000000101",
          borrower_label: "Elena Martinez",
          loan_number: "DEMO-1001",
          stage_label: "Underwriting",
          open_conditions: [
            {
              condition_id: "00000000-0000-0000-0000-000000000902",
              text: "Provide signed letter of explanation for recent inquiry",
            },
          ],
        },
      ],
      recent_items: [],
      expires_at: expires,
    };
  }
}

export async function uploadToPublicDropPortal(
  token: string,
  payload: PublicDropUploadPayload,
): Promise<{
  batch_id: string;
  items: SmartDropItem[];
  borrower_groups: SmartDropInboxResponse["borrower_groups"];
}> {
  const trimmed = token.trim();
  try {
    const res = await api<
      | {
          batch_id?: string;
          items?: SmartDropItem[];
          borrower_groups?: SmartDropInboxResponse["borrower_groups"];
        }
      | SmartDropItem[]
    >(`/api/public/drop/${encodeURIComponent(trimmed)}/upload`, {
      method: "POST",
      body: payload,
    });

    const items = Array.isArray(res) ? res : (res.items ?? []);
    const batch_id =
      !Array.isArray(res) && res.batch_id ? res.batch_id : `batch_${Date.now()}`;
    const borrower_groups =
      (!Array.isArray(res) ? res.borrower_groups : undefined) ??
      groupItemsByBorrower(items).map((g) => ({
        borrower_name: g.borrowerName,
        lo_name: g.loName,
        company_name: g.companyName,
        matched_loan_id: g.matchedLoanId,
        item_count: g.items.length,
        routed_seats: g.routedSeats,
      }));

    return {
      batch_id,
      items,
      borrower_groups,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status >= 400 && trimmed !== "lsdrop_demo") {
      throw err;
    }
    if (!trimmed.startsWith("lsdrop_")) {
      throw err;
    }
    const batch_id = `batch_${Date.now()}`;
    const nowIso = new Date().toISOString();
    const role: PortalRole =
      payload.uploader_role === "borrower" ||
      payload.uploader_role === "loan_processor"
        ? payload.uploader_role
        : "broker_lo";
    const items: SmartDropItem[] = payload.files.map((f, idx) => {
      const preview = previewFileClassification(
        f.file_name,
        f.text_excerpt,
        payload.borrower_name,
      );
      return {
        id: `item_${Date.now()}_${idx}`,
        link_id: `link_${trimmed.slice(0, 14)}`,
        batch_id,
        uploader_role: role,
        uploader_label:
          payload.uploader_label || `${PORTAL_ROLE_LABEL[role]} (Smart Drop)`,
        person_id: null,
        company_id: null,
        lo_name: "Partner Loan Officer",
        company_name: "Pacific Crest Mortgage",
        owner_user_id: null,
        loan_id: payload.loan_id ?? null,
        matched_condition_id:
          payload.matched_condition_id ?? payload.condition_id ?? null,
        file_name: f.file_name,
        mime_type: f.mime_type || "application/pdf",
        byte_size: f.byte_size || 1024,
        sha256: `sha256_${idx}_${f.file_name.length}`,
        detected_borrower_name: preview.detectedBorrowerName,
        detected_property_address: payload.property_address ?? null,
        detected_loan_number: null,
        doc_category: preview.docCategory,
        doc_type_label: preview.docTypeLabel,
        four_legs_pillar: preview.fourLegsPillar,
        routed_seat: preview.routedSeat,
        routed_user_id: null,
        routed_user_name: null,
        routing_reason: preview.routingReason,
        confidence: preview.segmentationGuardTriggered ? "review_needed" : "high",
        extracted_highlights: preview.extractedHighlights as unknown as Record<
          string,
          unknown
        >,
        review_status: "pending_review",
        review_note: payload.note ?? null,
        created_at: nowIso,
      };
    });
    const borrower_groups = groupItemsByBorrower(items).map((g) => ({
      borrower_name: g.borrowerName,
      lo_name: g.loName,
      company_name: g.companyName,
      matched_loan_id: g.matchedLoanId,
      item_count: g.items.length,
      routed_seats: g.routedSeats,
    }));
    return { batch_id, items, borrower_groups };
  }
}

export async function listSmartDropLinks(params?: {
  person_id?: string;
  loan_id?: string;
}): Promise<{ links: SmartDropLink[] }> {
  const q = new URLSearchParams();
  if (params?.person_id) q.set("person_id", params.person_id);
  if (params?.loan_id) q.set("loan_id", params.loan_id);
  const qs = q.toString();
  const res = await api<{ links?: SmartDropLink[]; items?: SmartDropLink[] } | SmartDropLink[]>(
    `/api/smart-drops/links${qs ? `?${qs}` : ""}`,
  );
  const rawLinks = Array.isArray(res) ? res : (res.links ?? res.items ?? []);
  const links = rawLinks.map((l) => ({
    ...l,
    token: l.token || l.token_cleartext || "",
    token_cleartext: l.token_cleartext || l.token || "",
  }));
  return { links };
}

export async function createSmartDropLink(payload: {
  portal_role: PortalRole;
  person_id?: string;
  company_id?: string;
  lo_name?: string;
  company_name?: string;
  loan_id?: string;
  deal_link_id?: string;
  label?: string;
  expires_in_days?: number;
}): Promise<SmartDropLink> {
  const res = await api<SmartDropLink | { link: SmartDropLink }>("/api/smart-drops/links", {
    method: "POST",
    body: payload,
  });
  const link = "link" in res && res.link ? res.link : (res as SmartDropLink);
  return {
    ...link,
    token: link.token || link.token_cleartext || "",
    token_cleartext: link.token_cleartext || link.token || "",
  };
}

export async function revokeSmartDropLink(id: string): Promise<void> {
  try {
    await api<void>(`/api/smart-drops/links/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
      await api<void>(`/api/smart-drops/links/${encodeURIComponent(id)}/revoke`, {
        method: "POST",
        body: {},
      });
      return;
    }
    throw err;
  }
}

export async function fetchSmartDropInbox(params?: {
  routed_seat?: string;
  person_id?: string;
  loan_id?: string;
  review_status?: string;
}): Promise<SmartDropInboxResponse> {
  const q = new URLSearchParams();
  if (params?.routed_seat) q.set("routed_seat", params.routed_seat);
  if (params?.person_id) q.set("person_id", params.person_id);
  if (params?.loan_id) q.set("loan_id", params.loan_id);
  if (params?.review_status) q.set("review_status", params.review_status);
  const qs = q.toString();
  const res = await api<Partial<SmartDropInboxResponse> | SmartDropItem[]>(
    `/api/smart-drops/inbox${qs ? `?${qs}` : ""}`,
  );
  const items = Array.isArray(res) ? res : (res.items ?? []);
  const counts_by_seat: Record<string, number> =
    !Array.isArray(res) && res.counts_by_seat
      ? res.counts_by_seat
      : {
          AE: items.filter((i) => i.routed_seat === "AE").length,
          CA: items.filter((i) => i.routed_seat === "CA").length,
          AM: items.filter((i) => i.routed_seat === "AM").length,
          Processor: items.filter((i) => i.routed_seat === "Processor").length,
        };
  const borrower_groups =
    (!Array.isArray(res) ? res.borrower_groups : undefined) ??
    groupItemsByBorrower(items).map((g) => ({
      borrower_name: g.borrowerName,
      lo_name: g.loName,
      company_name: g.companyName,
      matched_loan_id: g.matchedLoanId,
      item_count: g.items.length,
      routed_seats: g.routedSeats,
    }));
  return { items, counts_by_seat, borrower_groups };
}

export async function uploadStaffSmartDrop(
  payload: StaffSmartDropUploadPayload,
): Promise<{ batch_id: string; items: SmartDropItem[] }> {
  return api<{ batch_id: string; items: SmartDropItem[] }>("/api/smart-drops/upload", {
    method: "POST",
    body: payload,
  });
}

export async function updateSmartDropItem(
  id: string,
  patch: Partial<
    Pick<
      SmartDropItem,
      | "review_status"
      | "loan_id"
      | "matched_condition_id"
      | "routed_seat"
      | "doc_category"
      | "detected_borrower_name"
      | "review_note"
    >
  >,
): Promise<SmartDropItem> {
  const res = await api<SmartDropItem | { item: SmartDropItem }>(
    `/api/smart-drops/items/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      body: patch,
    },
  );
  return "item" in res && res.item ? res.item : (res as SmartDropItem);
}

export interface AuditCertificatePdfParams {
  request_id?: string | null;
  borrower_name?: string | null;
  signer_email?: string | null;
  document_title?: string | null;
  signature_type?: "drawn" | "typed" | string | null;
  document_sha256?: string | null;
  seal_sha256?: string | null;
}

/**
 * Builds the public token-authenticated endpoint URL for downloading a Tamper-Evident Audit
 * Certificate PDF (`GET /api/sign/:token/certificate.pdf`).
 */
export function buildAuditCertificatePdfUrl(
  tokenOrEnvelopeId: string,
  params?: AuditCertificatePdfParams,
): string {
  const cleanToken = tokenOrEnvelopeId.trim() || "demo_token";
  const q = new URLSearchParams();
  if (params?.request_id) q.set("request_id", params.request_id);
  if (params?.borrower_name) q.set("borrower_name", params.borrower_name);
  if (params?.signer_email) q.set("signer_email", params.signer_email);
  if (params?.document_title) q.set("document_title", params.document_title);
  if (params?.signature_type) q.set("signature_type", params.signature_type);
  if (params?.document_sha256) q.set("document_sha256", params.document_sha256);
  if (params?.seal_sha256) q.set("seal_sha256", params.seal_sha256);
  const qs = q.toString();
  return `/api/sign/${encodeURIComponent(cleanToken)}/certificate.pdf${qs ? `?${qs}` : ""}`;
}

export interface PosFourLegsHandoffPayload {
  borrower_name?: string | null;
  occupancy?: string | null;
  loan_purpose?: string | null;
  property_state?: string | null;
  property_type?: string | null;
  estimated_credit_score?: string | number | null;
  income_doc_type?: string | null;
  requested_loan_amount?: string | null;
  estimated_property_value?: string | null;
  reserves_months?: string | number | null;
  lo_name?: string | null;
  company_name?: string | null;
  person_id?: string | null;
  token?: string | null;
}

/**
 * Builds a deep link to `/prospecting?pos_handoff=1&...` that opens the Four Legs Scenario Desk
 * pre-populated with the Co-Branded POS intake scenario facts (`lending_decision: not_made`).
 */
export function buildPosFourLegsHandoffUrl(
  payload: PosFourLegsHandoffPayload,
  origin?: string,
): string {
  const q = new URLSearchParams();
  q.set("pos_handoff", "1");
  if (payload.borrower_name?.trim()) q.set("borrower_name", payload.borrower_name.trim());
  if (payload.occupancy?.trim()) q.set("occupancy", payload.occupancy.trim());
  if (payload.loan_purpose?.trim()) q.set("loan_purpose", payload.loan_purpose.trim());
  if (payload.property_state?.trim()) q.set("property_state", payload.property_state.trim());
  if (payload.property_type?.trim()) q.set("property_type", payload.property_type.trim());
  if (
    payload.estimated_credit_score !== undefined &&
    payload.estimated_credit_score !== null &&
    String(payload.estimated_credit_score).trim() !== ""
  ) {
    q.set("estimated_credit_score", String(payload.estimated_credit_score).trim());
  }
  if (payload.income_doc_type?.trim()) q.set("income_doc_type", payload.income_doc_type.trim());
  if (payload.requested_loan_amount?.trim()) {
    q.set("requested_loan_amount", payload.requested_loan_amount.trim());
  }
  if (payload.estimated_property_value?.trim()) {
    q.set("estimated_property_value", payload.estimated_property_value.trim());
  }
  if (
    payload.reserves_months !== undefined &&
    payload.reserves_months !== null &&
    String(payload.reserves_months).trim() !== ""
  ) {
    q.set("reserves_months", String(payload.reserves_months).trim());
  }
  if (payload.lo_name?.trim()) q.set("lo_name", payload.lo_name.trim());
  if (payload.company_name?.trim()) q.set("company_name", payload.company_name.trim());
  if (payload.person_id?.trim()) q.set("person_id", payload.person_id.trim());
  if (payload.token?.trim()) q.set("token", payload.token.trim());
  const path = `/prospecting?${q.toString()}`;
  return origin ? `${origin.replace(/\/$/, "")}${path}` : path;
}

